import { decompressFromBase64 } from 'lz-string';
import React, { FC, useEffect, useMemo, useState, useTransition } from 'react';
import ContentLoader from 'react-content-loader';
import { fetchQuery, graphql, useLazyLoadQuery, useMutation, useRelayEnvironment } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import { Checkbox } from 'src/components/widgets/checkbox';
import { QuestionDialog, useDialogManager } from 'src/components/widgets/dialog';
import { T, backofficeUrl, toGlobalID, toLocalID } from 'src/shared/helpers';
import theme from 'src/shared/theme';
import { productSidePanelContent_AssignPricingMutation } from './__generated__/productSidePanelContent_AssignPricingMutation.graphql';
import type { productSidePanelContent_AssignPrintMethodMutation } from './__generated__/productSidePanelContent_AssignPrintMethodMutation.graphql';
import type { productSidePanelContent_ConfiguratorModelQuery } from './__generated__/productSidePanelContent_ConfiguratorModelQuery.graphql';
import type { productSidePanelContent_LibraryMaterialQuery } from './__generated__/productSidePanelContent_LibraryMaterialQuery.graphql';
import type { productSidePanelContent_ProductQuery } from './__generated__/productSidePanelContent_ProductQuery.graphql';
import { productSidePanelContent_RemoveAIStagingMutation } from './__generated__/productSidePanelContent_RemoveAIStagingMutation.graphql';
import { productSidePanelContent_RemoveAIVirtualModelMutation } from './__generated__/productSidePanelContent_RemoveAIVirtualModelMutation.graphql';
import { productSidePanelContent_RemoveAreaBgMutation } from './__generated__/productSidePanelContent_RemoveAreaBgMutation.graphql';
import { productSidePanelContent_RemoveGridRuleMutation } from './__generated__/productSidePanelContent_RemoveGridRuleMutation.graphql';
import { productSidePanelContent_RemoveMoqMutation } from './__generated__/productSidePanelContent_RemoveMoqMutation.graphql';
import { productSidePanelContent_RemovePricingMutation } from './__generated__/productSidePanelContent_RemovePricingMutation.graphql';
import { productSidePanelContent_RemoveProfanityMutation } from './__generated__/productSidePanelContent_RemoveProfanityMutation.graphql';
import type { productSidePanelContent_RemoveQuoteRuleMutation } from './__generated__/productSidePanelContent_RemoveQuoteRuleMutation.graphql';
import type { productSidePanelContent_SceneDetailQuery } from './__generated__/productSidePanelContent_SceneDetailQuery.graphql';
import type { productSidePanelContent_SetStatusProductMutation } from './__generated__/productSidePanelContent_SetStatusProductMutation.graphql';
import type { productSidePanelContent_ToolDataQuery } from './__generated__/productSidePanelContent_ToolDataQuery.graphql';
import type { productSidePanelContent_UnassignPrintMethodMutation } from './__generated__/productSidePanelContent_UnassignPrintMethodMutation.graphql';
import { AddAIStagingRulePanel } from './addPanels/addAIStagingRulePanel';
import { AddAIVirtualModelRulePanel } from './addPanels/addAIVirtualModelRulePanel';
import { AddAreaBackgroundRulePanel } from './addPanels/addAreaBackgroundRulePanel';
import { AddCollageRulePanel } from './addPanels/addCollageRulePanel';
import { AddMoqRulePanel } from './addPanels/addMoqRulePanel';
import { AddPricingRulePanel } from './addPanels/addPricingRulePanel';
import { AddPrintingMethodPanel } from './addPanels/addPrintingMethodPanel';
import { AddProfanityRulePanel } from './addPanels/addProfanityRulePanel';
import { AddQuoteRulePanel } from './addPanels/addQuoteRulePanel';
import { CollapsibleSection } from './common/collapsibleSection';
import {
    BOOLEAN_TOOLS,
    TOOL_CATEGORY_ORDER,
    getAllSalesChannels,
    getSalesChannelCount,
    getToolInfo,
    getToolLabel,
    useToolCategoryLabels,
    useToolDefinitions
} from './common/constants';
import {
    AddChannelButton,
    ChannelDot,
    ChannelItem,
    ChannelMeta,
    ChannelsBadge,
    ChannelsDropdown,
    ConditionalBadge,
    DeleteCard,
    DeleteConfirmActions,
    DeleteConfirmText,
    DeleteOverlay,
    EmptyState,
    FilterBar,
    FilterBarLink,
    FooterButton,
    GroupLabel,
    HeaderMeta,
    HeaderPreviewButton,
    HeaderSep,
    ImagePlaceholder,
    InfoIcon,
    InlineSkeleton,
    ItemAction,
    ItemActionDanger,
    ItemDot,
    ItemMeta,
    ItemMetaPushed,
    ItemName,
    ItemNameAction,
    ItemPreviewImg,
    LearnMoreLink,
    OverviewBody,
    OverviewCard,
    OverviewCardBadge,
    OverviewCardEmpty,
    OverviewCardHeader,
    OverviewCardItem,
    OverviewCardTitle,
    OverviewCategoryTitle,
    OverviewGrid,
    PanelBody,
    PanelFooter,
    PanelHeaderContent,
    PanelHeaderImg,
    PanelHeaderInfo,
    PanelItem,
    PanelItemDimmed,
    PanelItemSpaced,
    PanelItemSpacedLarge,
    SidePanelDrawer,
    SidePanelScrollArea,
    StatusBadge,
    StatusBar,
    StatusBarSpacer,
    StructureSection,
    StructureSectionHeader,
    StructureSectionTitle,
    StructureSummaryCard,
    StructureSummaryGrid,
    StructureSummaryLabel,
    StructureSummaryValue,
    StructureTransitionPanel,
    StructureTransitionWrapper,
    StructureTree,
    StructureTreeToggle,
    ToolBadge,
    TreeIcon,
    TreeManageLink,
    TreePrintArea,
    TreeSide,
    TreeSideFlush,
    TreeVariant,
    TreeVariantHeader
} from './common/styled';
import type { ProductSidePanelProps, ProductToolData, SidePanelProduct } from './common/types';
import { ComposerAttributeEditor } from './editors/composerAttributeEditor';
import { GenericRuleEditor } from './editors/genericRuleEditor';
import { PricingRuleEditor } from './editors/pricingRuleEditor';
import { PrintingMethodEditor } from './editors/printingMethodEditor';

// ========== Queries ==========
const sidePanelProductQuery = graphql`
    query productSidePanelContent_ProductQuery($id: ID!) {
        node(id: $id) {
            ... on Model {
                id
                modelID
                name
                code
                statusID
                smallImageUrl
                zakekeEnvironment
                provider
                canAddAdditionalCode
                sourceProductTemplateID
                modelColorsCompressed
                salesChannel {
                    id
                    detail {
                        id
                        properties {
                            name
                        }
                    }
                    ecommerceSettings {
                        ecommerceName
                        remoteUrl
                        productsApi
                        hasProductsApi
                        definitiveUrl
                    }
                }
                additionalCodes {
                    code
                    salesChannelID
                    merchantProductName
                    salesChannel {
                        id
                        detail {
                            id
                            properties {
                                name
                            }
                        }
                        ecommerceSettings {
                            ecommerceName
                            remoteUrl
                            productsApi
                            hasProductsApi
                            definitiveUrl
                        }
                    }
                }
                printingMethods {
                    id
                    printTypeID
                    name
                    restrictions {
                        showImageProvider
                        uploadRestrictions {
                            isUserImageAllowed
                        }
                    }
                }
                scenes {
                    sceneID
                }
                areaBackgroundRules {
                    productRule {
                        colorList
                    }
                }
                quoteRule {
                    id
                    quoteRuleID
                    name
                }
                moqRules {
                    productRule {
                        id
                        moqRuleID
                        name
                    }
                }
            }
        }
    }
`;
const sidePanelToolDataQuery = graphql`
    query productSidePanelContent_ToolDataQuery($productId: Long!, $modelId: ID!, $pricingFirst: Int!) {
        designTemplates(productId: $productId) {
            designTemplateId
            name
            previewUrl
        }
        shapesAssignmentsForProduct(productID: $productId) {
            forProduct
            printTypesIDs
        }
        pricingsDetailed(productID: $productId, first: $pricingFirst) {
            edges {
                node {
                    id
                    pricingName
                    pricingID
                }
            }
        }
        profanityFilterRulesForProduct(productID: $productId) {
            productRule {
                ruleID
                ruleName
            }
            printTypeRules {
                key
                value {
                    ruleID
                    ruleName
                }
            }
        }
        aiProductStagingRulesByProduct(productID: $productId) {
            id
            name
        }
        aiVirtualModelRulesByProduct(productID: $productId) {
            id
            name
        }
        gridRulesByProduct(productID: $productId) {
            id
            name
        }
        areaBackgroundRulesByProduct(productID: $productId) {
            id
            name
        }
        additionalAttributesByProduct(productID: $modelId) {
            id
            additionalAttributeID
            name
        }
        profanityFilterRules(first: 1) {
            nodes {
                id
            }
        }
        printTypes(first: 200) {
            edges {
                node {
                    id
                    printTypeID
                    name
                }
            }
        }
        pricings(first: 200) {
            edges {
                node {
                    id
                    pricingName
                    pricingID
                }
            }
        }
        allAreaBackgroundRules: areaBackgroundRules {
            areaBackgroundRuleID
        }
        gridRules {
            gridRuleID
        }
        placementsOutput(id: $modelId) {
            placementOutputID
        }
        toolUsageRuleByToolType(toolType: CustomizerAIImageGenerator) {
            toolUsageRuleID
        }
        designWithAISettings {
            settingsID
        }
        productMultipleVariantsRule(productId: $modelId) {
            attributeCodes
        }
        allAIStagingRules: aiProductStagingRules {
            ruleID
        }
        allAIVirtualModelRules: aiVirtualModelRules {
            ruleID
        }
        allMoqRules: moqRules(first: 1) {
            edges {
                node {
                    id
                }
            }
        }
        allQuoteRules: quoteRulesList {
            quoteRuleID
        }
    }
`;
export const ProductSidePanelContent: FC<ProductSidePanelProps> = ({
    productId,
    onClose,
    onDelete,
    onPreview,
    onPublishToggle,
    onDuplicate,
    onAddSalesChannel,
    onToolAssignmentChange
}) => {
    // ========== Hooks ==========
    const navigate = useNavigate();
    const environment = useRelayEnvironment();
    const { showDialog, closeDialog } = useDialogManager();
    const TOOL_CATEGORY_LABELS = useToolCategoryLabels();
    const TOOL_DEFINITIONS = useToolDefinitions();

    // ========== UI State ==========
    const [channelsOpen, setChannelsOpen] = useState(false);
    const [expandAllKey, setExpandAllKey] = useState(0);
    const [openSections, setOpenSections] = useState<Set<string>>(new Set());
    const [hideUnconfigured, setHideUnconfigured] = useState(() => {
        try {
            return localStorage.getItem('zakeke_hideUnconfiguredTools') === 'true';
        } catch {
            return false;
        }
    });
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [showStructureTree, setShowStructureTree] = useState(false);
    const [showComposerTree, setShowComposerTree] = useState(false);
    const [expandedVariants, setExpandedVariants] = useState<Set<number>>(new Set());
    const [optimisticStatusId, setOptimisticStatusId] = useState<number | null>(null);
    const [isPublishing, setIsPublishing] = useState(false);

    // ========== Panel & Editor State ==========
    const [stackedEditor, setStackedEditor] = useState<{
        type: string;
        name: string;
        id: string;
        filterMode?: 'all' | 'attribute' | 'option';
        optionName?: string;
    } | null>(null);
    const [showAddPrintingMethod, setShowAddPrintingMethod] = useState(false);
    const [showAddPricingRule, setShowAddPricingRule] = useState(false);
    const [showAddCollageRule, setShowAddCollageRule] = useState(false);
    const [showAddAreaBackgroundRule, setShowAddAreaBackgroundRule] = useState(false);
    const [showAddProfanityRule, setShowAddProfanityRule] = useState(false);
    const [showAddAIStagingRule, setShowAddAIStagingRule] = useState(false);
    const [showAddAIVirtualModelRule, setShowAddAIVirtualModelRule] = useState(false);
    const [showAddMoqRule, setShowAddMoqRule] = useState(false);
    const [showAddQuoteRule, setShowAddQuoteRule] = useState(false);

    // ========== Data Refresh State ==========
    const [dataRefreshKey, setDataRefreshKey] = useState(0);
    const [unassigningItemId, setUnassigningItemId] = useState<string | null>(null);
    const [isUnassigning, startUnassignTransition] = useTransition();
    const [configuratorData, setConfiguratorData] = useState<{
        sceneMeshes: { meshID: string; name: string; sides: { sideID: number }[]; materials: { name: string; effectID: string }[] }[];
        materialsCount: number;
        attributes: {
            name: string;
            code: string;
            description: string;
            options: {
                optionID: number | null;
                name: string;
                optionCode: string;
                description: string;
                imageUrl: string | null;
                actionsCount: number;
                hasLink: boolean;
                price: number | null;
                actions: {
                    actionID: number;
                    name: string | null;
                    sceneElementTypeID: number;
                    actionTypeID: number;
                    selector: string | null;
                    propertyName: string | null;
                    value: string | null;
                }[];
            }[];
            actionsCount: number;
            attrActions: {
                actionID: number;
                name: string | null;
                sceneElementTypeID: number;
                actionTypeID: number;
                selector: string | null;
                propertyName: string | null;
                value: string | null;
            }[];
        }[];
    } | null>(null);

    // ========== Mutations ==========

    const [commitSetStatus] = useMutation<productSidePanelContent_SetStatusProductMutation>(graphql`
        mutation productSidePanelContent_SetStatusProductMutation($input: SetStatusProductInput!) {
            setStatusProduct(input: $input) {
                model {
                    id
                    statusID
                }
                errors {
                    ... on LiquidSintaxError {
                        message
                    }
                }
            }
        }
    `);

    const [commitAssignPrintMethod] = useMutation<productSidePanelContent_AssignPrintMethodMutation>(graphql`
        mutation productSidePanelContent_AssignPrintMethodMutation($input: AddOrRemoveAssignmentPrintTypeToProductsInput!) {
            addOrRemoveAssignmentPrintTypeToProducts(input: $input)
        }
    `);

    const [commitUnassignPrintMethod] = useMutation<productSidePanelContent_UnassignPrintMethodMutation>(graphql`
        mutation productSidePanelContent_UnassignPrintMethodMutation($input: AddOrRemoveAssignmentPrintTypeToProductsInput!) {
            addOrRemoveAssignmentPrintTypeToProducts(input: $input)
        }
    `);

    const [commitAssignPricing] = useMutation<productSidePanelContent_AssignPricingMutation>(graphql`
        mutation productSidePanelContent_AssignPricingMutation($pricingId: ID!, $productIds: [Long!]!) {
            assignProductsToPricing(pricingId: $pricingId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitRemovePricing] = useMutation<productSidePanelContent_RemovePricingMutation>(graphql`
        mutation productSidePanelContent_RemovePricingMutation($pricingId: ID!, $productIds: [Long!]!) {
            removeProductsFromPricing(pricingId: $pricingId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitRemoveMoq] = useMutation<productSidePanelContent_RemoveMoqMutation>(graphql`
        mutation productSidePanelContent_RemoveMoqMutation($moqRuleId: ID!, $productIds: [Long!]!) {
            removeProductsFromMoqRule(moqRuleId: $moqRuleId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitRemoveAIStaging] = useMutation<productSidePanelContent_RemoveAIStagingMutation>(graphql`
        mutation productSidePanelContent_RemoveAIStagingMutation($ruleId: ID!, $productIds: [Long!]!) {
            removeProductsFromAIStagingRule(ruleId: $ruleId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitRemoveAIVirtualModel] = useMutation<productSidePanelContent_RemoveAIVirtualModelMutation>(graphql`
        mutation productSidePanelContent_RemoveAIVirtualModelMutation($ruleId: ID!, $productIds: [Long!]!) {
            removeProductsFromAIVirtualModelRule(ruleId: $ruleId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitRemoveProfanity] = useMutation<productSidePanelContent_RemoveProfanityMutation>(graphql`
        mutation productSidePanelContent_RemoveProfanityMutation($profanityRuleId: ID!, $productIds: [Long!]!) {
            removeProductsFromProfanityRule(profanityRuleId: $profanityRuleId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitRemoveGridRule] = useMutation<productSidePanelContent_RemoveGridRuleMutation>(graphql`
        mutation productSidePanelContent_RemoveGridRuleMutation($gridRuleId: ID!, $productIds: [Long!]) {
            removeProductsFromGridRule(gridRuleId: $gridRuleId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitRemoveAreaBg] = useMutation<productSidePanelContent_RemoveAreaBgMutation>(graphql`
        mutation productSidePanelContent_RemoveAreaBgMutation($ruleId: ID!, $productIds: [Long!]!) {
            removeProductsFromAreaBackgroundRule(ruleId: $ruleId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitRemoveQuoteRule] = useMutation<productSidePanelContent_RemoveQuoteRuleMutation>(graphql`
        mutation productSidePanelContent_RemoveQuoteRuleMutation($ruleId: ID!, $productIds: [Long!]!) {
            removeProductsFromQuoteRule(ruleId: $ruleId, productIds: $productIds) {
                result
            }
        }
    `);

    // ========== Data Queries ==========
    const data = useLazyLoadQuery<productSidePanelContent_ProductQuery>(
        sidePanelProductQuery,
        {
            id: productId
        },
        { fetchPolicy: 'store-and-network', fetchKey: dataRefreshKey }
    );
    const product = data.node as SidePanelProduct;

    // ========== Derived Data (Product) ==========

    const allProductColors = React.useMemo(() => {
        const compressed = product?.modelColorsCompressed;
        if (!compressed) return [];
        try {
            return JSON.parse(decompressFromBase64(compressed)) as {
                colorID: number;
                name: string;
                code: string | null;
                colorCode: string | null;
                sides?: { name?: string; imageUrl?: string; areas?: { name?: string }[] }[];
            }[];
        } catch {
            return [];
        }
    }, [product?.modelColorsCompressed]);

    const productColors = React.useMemo(() => allProductColors.filter((color) => color.colorID !== 1), [allProductColors]);

    const productStructureSummary = React.useMemo(() => {
        const totalVariants = productColors.length;
        const source = allProductColors.length > 0 ? allProductColors : [];
        const maxSides = source.reduce((max, color) => Math.max(max, color.sides?.length || 0), 0);
        const maxPrintAreas = source.reduce((max, color) => {
            const variantAreas = (color.sides || []).reduce((sum, side) => sum + (side.areas?.length || 0), 0);
            return Math.max(max, variantAreas);
        }, 0);
        return { totalVariants, maxSides, maxPrintAreas };
    }, [productColors, allProductColors]);

    const toolDataQuery = useLazyLoadQuery<productSidePanelContent_ToolDataQuery>(
        sidePanelToolDataQuery,
        {
            productId: product.modelID,
            modelId: product.id,
            pricingFirst: 50
        },
        { fetchPolicy: 'network-only', fetchKey: dataRefreshKey }
    );

    // ========== Derived Data (Tool Data) ==========

    const accountPrintMethods = useMemo(() => {
        const edges = toolDataQuery?.printTypes?.edges || [];
        return edges.map((edge) => ({
            id: edge.node.id,
            numericId: String(edge.node.printTypeID),
            name: edge.node.name
        }));
    }, [toolDataQuery]);

    const accountPricingRules = useMemo(() => {
        const edges = toolDataQuery?.pricings?.edges || [];
        return edges.map((edge) => ({
            id: edge.node.id,
            name: edge.node.pricingName || `#${edge.node.pricingID}`
        }));
    }, [toolDataQuery]);

    const accountHasProfanityRules = (toolDataQuery?.profanityFilterRules?.nodes?.length ?? 0) > 0;
    const accountHasAreaBackgroundRules = (toolDataQuery?.allAreaBackgroundRules?.length ?? 0) > 0;
    const accountHasCollageRules = (toolDataQuery?.gridRules?.length ?? 0) > 0;
    const accountHasAIStagingRules = (toolDataQuery?.allAIStagingRules?.length ?? 0) > 0;
    const accountHasAIVirtualModelRules = (toolDataQuery?.allAIVirtualModelRules?.length ?? 0) > 0;
    const accountHasMoqRules = (toolDataQuery?.allMoqRules?.edges?.length ?? 0) > 0;
    const accountHasQuoteRules = (toolDataQuery?.allQuoteRules?.length ?? 0) > 0;

    const placementConfigured = useMemo(() => (toolDataQuery?.placementsOutput || []).length > 0, [toolDataQuery]);
    const aiImageGenConfigured = useMemo(() => !!toolDataQuery?.toolUsageRuleByToolType, [toolDataQuery]);
    const aiDesignGenConfigured = useMemo(() => (toolDataQuery?.designWithAISettings?.length ?? 0) > 0, [toolDataQuery]);
    const bulkVariationConfigured = useMemo(() => (toolDataQuery?.productMultipleVariantsRule?.attributeCodes || []).length > 0, [toolDataQuery]);

    const toolData = React.useMemo<ProductToolData>(() => {
        const moqRule = product.moqRules?.productRule;
        return {
            loaded: true,
            templates: (toolDataQuery.designTemplates || []).map((template) => ({
                id: template.designTemplateId.toString(),
                name: template.name,
                previewUrl: template.previewUrl
            })),
            shapesConfigured: !!(
                toolDataQuery.shapesAssignmentsForProduct?.forProduct || (toolDataQuery.shapesAssignmentsForProduct?.printTypesIDs?.length ?? 0) > 0
            ),
            pricingRules: (toolDataQuery.pricingsDetailed?.edges || []).map((edge) => ({
                id: edge.node.id,
                name: edge.node.pricingName || `#${edge.node.pricingID}`
            })),
            profanityRules: (() => {
                const rule = toolDataQuery.profanityFilterRulesForProduct?.productRule;
                if (!rule) return [];
                return [{ id: toGlobalID(rule.ruleID, 'ProfanityRule'), name: rule.ruleName || `#${rule.ruleID}` }];
            })(),
            aiStagingRules: (toolDataQuery.aiProductStagingRulesByProduct || []).map((rule) => ({ id: rule.id, name: rule.name })),
            aiVirtualModelRules: (toolDataQuery.aiVirtualModelRulesByProduct || []).map((rule) => ({ id: rule.id, name: rule.name })),
            has3DScene: !!(product.scenes?.length && product.scenes[0]?.sceneID),
            areaBackgroundRules: (toolDataQuery.areaBackgroundRulesByProduct || []).map((rule) => ({
                id: rule.id,
                name: rule.name || ''
            })),
            quoteRule: product.quoteRule ? { id: product.quoteRule.id, name: product.quoteRule.name || `#${product.quoteRule.quoteRuleID}` } : null,
            moqRules: moqRule ? [{ id: moqRule.id, name: moqRule.name || `#${moqRule.moqRuleID}` }] : [],
            collageRules: (toolDataQuery.gridRulesByProduct || []).filter(Boolean).map((rule) => ({
                id: rule!.id!,
                name: rule!.name || ''
            })),
            bulkVariationEnabled: bulkVariationConfigured,
            extraOptions: toolDataQuery.additionalAttributesByProduct
                ? [
                      {
                          id: String(toolDataQuery.additionalAttributesByProduct.additionalAttributeID),
                          name: toolDataQuery.additionalAttributesByProduct.name || `#${toolDataQuery.additionalAttributesByProduct.additionalAttributeID}`
                      }
                  ]
                : [],
            placementConfigured,
            aiImageGenConfigured,
            aiDesignGenConfigured,
            imageRestrictions: (product.printingMethods || []).map((pm) => ({
                printMethodId: pm.id,
                printMethodName: pm.name,
                userUpload: pm.restrictions?.uploadRestrictions?.isUserImageAllowed !== false,
                clipartGalleries: pm.restrictions?.showImageProvider !== false
            }))
        };
    }, [toolDataQuery, product, placementConfigured, aiImageGenConfigured, aiDesignGenConfigured, bulkVariationConfigured]);

    // ========== Effects ==========

    useEffect(() => {
        if (product.zakekeEnvironment !== 'COMPOSER') return;
        (async () => {
            try {
                const json = await fetchQuery<productSidePanelContent_ConfiguratorModelQuery>(
                    environment,
                    graphql`
                        query productSidePanelContent_ConfiguratorModelQuery($id: ID!) {
                            node(id: $id) {
                                ... on Model {
                                    scenes {
                                        sceneID
                                        meshSides {
                                            meshID
                                            sideID
                                        }
                                        meshDesignEffects {
                                            effectID
                                            meshID
                                            subMeshID
                                            name
                                            materialID
                                        }
                                    }
                                    attributes {
                                        name
                                        attributeCode
                                        description
                                        options {
                                            optionID
                                            name
                                            optionCode
                                            description
                                            imageUrl
                                            actions {
                                                actionID
                                                name
                                                sceneElementTypeID
                                                actionTypeID
                                                selector
                                                propertyName
                                                value
                                            }
                                            constraintExpression {
                                                op
                                            }
                                        }
                                        pricings {
                                            attributePricingID
                                            selectedOptionID
                                            price
                                        }
                                        preActions {
                                            actionID
                                            name
                                            sceneElementTypeID
                                            actionTypeID
                                            selector
                                            propertyName
                                            value
                                        }
                                        valueActions {
                                            actionID
                                            name
                                            sceneElementTypeID
                                            actionTypeID
                                            selector
                                            propertyName
                                            value
                                        }
                                        postActions {
                                            actionID
                                            name
                                            sceneElementTypeID
                                            actionTypeID
                                            selector
                                            propertyName
                                            value
                                        }
                                    }
                                }
                            }
                        }
                    `,
                    { id: product.id }
                ).toPromise();
                const model = json?.node;
                const scenes = model?.scenes || [];
                // Fetch full Scene data (meshLocations, cameraLocations, materialMappings) for each scene
                let allMeshLocations: { locationID: string; name: string }[] = [];
                let allMaterialMappings: { sceneMaterialID: string; libraryMaterialID: string }[] = [];
                let allShaderMaterials: { materialID: string; name: string }[] = [];
                let allTextureMaterialIds = new Set<string>();
                let allSources: { url: string; rootUrl: string; sceneFileName: string }[] = [];
                if (scenes.length > 0) {
                    try {
                        const sceneResults = await Promise.all(
                            scenes.map((s: { sceneID: string }) => {
                                const sceneGlobalId = `guid://Zakeke/Scene/${s.sceneID}`;
                                return fetchQuery<productSidePanelContent_SceneDetailQuery>(
                                    environment,
                                    graphql`
                                        query productSidePanelContent_SceneDetailQuery($id: String!) {
                                            scene(id: $id) {
                                                meshLocations {
                                                    locationID
                                                    name
                                                }
                                                materialMappings {
                                                    sceneMaterialID
                                                    libraryMaterialID
                                                }
                                                shaderMaterials {
                                                    materialID
                                                    name
                                                }
                                                textures {
                                                    materialID
                                                }
                                                sources {
                                                    sourceID
                                                    url
                                                    rootUrl
                                                    sceneFileName
                                                }
                                            }
                                        }
                                    `,
                                    { id: sceneGlobalId }
                                ).toPromise();
                            })
                        );
                        for (let i = 0; i < scenes.length; i++) {
                            const sceneData = sceneResults[i]?.scene;
                            if (sceneData) {
                                allMeshLocations = allMeshLocations.concat(sceneData.meshLocations || []);
                                allMaterialMappings = allMaterialMappings.concat(sceneData.materialMappings || []);
                                allShaderMaterials = allShaderMaterials.concat(sceneData.shaderMaterials || []);
                                allSources = allSources.concat(sceneData.sources || []);
                                for (const tex of sceneData.textures || []) {
                                    if (tex.materialID) allTextureMaterialIds.add(tex.materialID);
                                }
                            }
                        }
                    } catch {
                        // Continue with Model-level data only
                    }
                }
                // Build mesh name map from meshLocations
                const meshNameMap = new Map<string, string>();
                for (const ml of allMeshLocations) {
                    meshNameMap.set(ml.locationID, ml.name);
                }
                // Group meshSides by meshID across all scenes
                const meshMap = new Map<string, { sideID: number }[]>();
                for (const scene of scenes) {
                    for (const ms of scene.meshSides || []) {
                        if (!meshMap.has(ms.meshID)) meshMap.set(ms.meshID, []);
                        meshMap.get(ms.meshID)!.push({ sideID: ms.sideID });
                    }
                }
                // If meshMap is empty but we have meshLocations, create entries from meshLocations
                if (meshMap.size === 0 && allMeshLocations.length > 0) {
                    for (const ml of allMeshLocations) {
                        if (!meshMap.has(ml.locationID)) meshMap.set(ml.locationID, []);
                    }
                }
                // Try to fetch mesh/material info from GLTF source file
                const meshMaterialsMap = new Map<string, { name: string; effectID: string }[]>();
                let totalEffects = 0;
                let gltfMeshNames: string[] = [];
                let gltfMaterialNames: string[] = [];
                if (allSources.length > 0) {
                    for (const source of allSources) {
                        if (gltfMeshNames.length > 0) break;
                        const fileUrl = source.rootUrl + source.sceneFileName;
                        if (!fileUrl) continue;
                        try {
                            const gltfRes = await fetch(fileUrl);
                            const gltfJson = await gltfRes.json();
                            // GLTF meshes — include children in nodes as meshes can be nested
                            if (gltfJson.meshes) {
                                gltfMeshNames = gltfJson.meshes.map((m: any) => m.name || 'Unnamed');
                            }
                            if (gltfJson.materials) {
                                gltfMaterialNames = gltfJson.materials.map((m: any) => m.name || 'Unnamed');
                            }
                        } catch {
                            // Not a JSON GLTF or fetch failed — continue with other sources
                        }
                    }
                }
                // Use GLTF data if available
                if (gltfMaterialNames.length > 0) {
                    const bucketKey = meshMap.keys().next().value || '__materials__';
                    if (!meshMap.has(bucketKey)) meshMap.set(bucketKey, []);
                    if (!meshMaterialsMap.has(bucketKey)) meshMaterialsMap.set(bucketKey, []);
                    const existing = meshMaterialsMap.get(bucketKey)!;
                    for (let i = 0; i < gltfMaterialNames.length; i++) {
                        existing.push({ name: gltfMaterialNames[i], effectID: `gltf-mat-${i}` });
                        totalEffects++;
                    }
                }
                // Fallback chain if no GLTF data
                if (totalEffects === 0 && allMaterialMappings.length > 0) {
                    const uniqueSceneMaterials = new Map<string, string>();
                    for (const m of allMaterialMappings) {
                        if (m.sceneMaterialID && !uniqueSceneMaterials.has(m.sceneMaterialID)) {
                            uniqueSceneMaterials.set(m.sceneMaterialID, m.libraryMaterialID);
                        }
                    }
                    let matNameMap = new Map<string, string>();
                    if (uniqueSceneMaterials.size > 0) {
                        try {
                            const entries = Array.from(uniqueSceneMaterials.entries());
                            const matResults = await Promise.all(
                                entries.map(([, libId]) => {
                                    const globalMatId = libId.includes('/') ? libId : `guid://Zakeke/LibraryMaterial/${libId}`;
                                    return fetchQuery<productSidePanelContent_LibraryMaterialQuery>(
                                        environment,
                                        graphql`
                                            query productSidePanelContent_LibraryMaterialQuery($id: UUID!) {
                                                libraryMaterial(id: $id) {
                                                    materialID
                                                    name
                                                }
                                            }
                                        `,
                                        { id: globalMatId }
                                    ).toPromise();
                                })
                            );
                            for (let i = 0; i < entries.length; i++) {
                                const mat = matResults[i]?.libraryMaterial;
                                if (mat?.name) matNameMap.set(entries[i][0], mat.name);
                            }
                        } catch {
                            /* */
                        }
                    }
                    const shaderNameMap = new Map<string, string>();
                    for (const sm of allShaderMaterials) shaderNameMap.set(sm.materialID, sm.name);
                    const bucketKey = meshMap.keys().next().value || '__materials__';
                    if (!meshMap.has(bucketKey)) meshMap.set(bucketKey, []);
                    if (!meshMaterialsMap.has(bucketKey)) meshMaterialsMap.set(bucketKey, []);
                    const existing = meshMaterialsMap.get(bucketKey)!;
                    for (const [sceneMaterialID] of uniqueSceneMaterials) {
                        const name = matNameMap.get(sceneMaterialID) || shaderNameMap.get(sceneMaterialID) || sceneMaterialID.substring(0, 8);
                        existing.push({ name, effectID: sceneMaterialID });
                        totalEffects++;
                    }
                }
                if (totalEffects === 0 && allShaderMaterials.length > 0) {
                    const bucketKey = meshMap.keys().next().value || '__materials__';
                    if (!meshMap.has(bucketKey)) meshMap.set(bucketKey, []);
                    if (!meshMaterialsMap.has(bucketKey)) meshMaterialsMap.set(bucketKey, []);
                    const existing = meshMaterialsMap.get(bucketKey)!;
                    for (const sm of allShaderMaterials) {
                        existing.push({ name: sm.name || sm.materialID.substring(0, 8), effectID: sm.materialID });
                        totalEffects++;
                    }
                }
                // Override meshMap with GLTF mesh data if available
                if (gltfMeshNames.length > 0) {
                    meshMap.clear();
                    for (let i = 0; i < gltfMeshNames.length; i++) {
                        const meshID = `gltf-mesh-${i}`;
                        meshMap.set(meshID, []);
                        meshNameMap.set(meshID, gltfMeshNames[i]);
                    }
                }
                const sceneMeshes = Array.from(meshMap.entries())
                    .filter(([meshID]) => meshID !== '__materials__')
                    .map(([meshID, sides]) => ({
                        meshID,
                        name: meshNameMap.get(meshID) || meshID.substring(0, 8),
                        sides,
                        materials: meshMaterialsMap.get(meshID) || []
                    }));
                const materialsCount = totalEffects;
                const mapAction = (act: {
                    actionID: number;
                    name?: string | null;
                    sceneElementTypeID: number;
                    actionTypeID: number;
                    selector?: string | null;
                    propertyName?: string | null;
                    value?: string | null;
                }) => ({
                    actionID: act.actionID,
                    name: act.name || null,
                    sceneElementTypeID: act.sceneElementTypeID,
                    actionTypeID: act.actionTypeID,
                    selector: act.selector || null,
                    propertyName: act.propertyName || null,
                    value: act.value || null
                });
                const attributes = (model?.attributes || []).map((a: any) => {
                    const pricings = a.pricings || [];
                    const priceByOptionId = new Map<number, number>();
                    pricings.forEach((p: any) => {
                        if (p.selectedOptionID != null) priceByOptionId.set(p.selectedOptionID, p.price);
                    });
                    return {
                        name: a.name || 'Unnamed',
                        code: a.attributeCode || '',
                        description: a.description || '',
                        options: (a.options || []).map((o: any) => ({
                            optionID: o.optionID ?? null,
                            name: o.name || 'Unnamed',
                            optionCode: o.optionCode || '',
                            description: o.description || '',
                            imageUrl: o.imageUrl || null,
                            actionsCount: o.actions?.length || 0,
                            hasLink: !!o.constraintExpression,
                            price: o.optionID != null ? (priceByOptionId.get(o.optionID) ?? null) : null,
                            actions: (o.actions || []).map(mapAction)
                        })),
                        actionsCount:
                            (a.preActions?.length || 0) +
                            (a.valueActions?.length || 0) +
                            (a.postActions?.length || 0) +
                            (a.options || []).reduce((s: number, o: any) => s + (o.actions?.length || 0), 0),
                        attrActions: [...(a.preActions || []), ...(a.valueActions || []), ...(a.postActions || [])].map(mapAction)
                    };
                });
                setConfiguratorData({ sceneMeshes, materialsCount, attributes });
            } catch {
                setConfiguratorData({ sceneMeshes: [], materialsCount: 0, attributes: [] });
            }
        })();
    }, [product.id, product.zakekeEnvironment, environment]);
    if (!product) return null;

    // ========== Computed Values ==========
    const isOnline = (optimisticStatusId ?? product.statusID) === 1;
    const salesChannels = getAllSalesChannels(product);
    const salesChannelCount = getSalesChannelCount(product);
    const availableTools = TOOL_DEFINITIONS.filter((t) => t.environments.includes(product.zakekeEnvironment));

    // ========== Event Handlers ==========

    const handleEditProduct = () => {
        if (product.zakekeEnvironment === 'COMPOSER') {
            window.location.href = `/Composer?id=${product.modelID}&page=product`;
        } else {
            navigate(backofficeUrl('/product?product_id=' + product.modelID));
        }
    };

    const handleOpenToolEditor = (toolKey: string, itemId: string) => {
        // Collage layout: navigate to backoffice page instead of opening compact editor
        if (toolKey === 'collage' && itemId) {
            navigate(backofficeUrl(`/tools/collage-layout/grid-rule/${toLocalID(itemId)}`));
            return;
        }
        setStackedEditor({ type: toolKey, name: toolKey, id: itemId });
    };

    const handleCloseStacked = () => {
        setStackedEditor(null);
        // Refetch data that may have changed in the stacked editor
        setDataRefreshKey((k) => k + 1);
    };

    const productState = {};

    const handleGoToFullTool = (route: string, id?: string) => {
        if (id) {
            navigate(backofficeUrl(route + '/' + id), { state: productState });
        } else {
            navigate(backofficeUrl(route), { state: productState });
        }
    };

    const handleGoToComposer = () => {
        window.location.href = `/Composer?id=${product.modelID}&page=product`;
    };
    const handlePublishToggle = async () => {
        if (isPublishing) return;
        const newStatusId = (optimisticStatusId ?? product.statusID) === 1 ? 0 : 1;
        setOptimisticStatusId(newStatusId);
        setIsPublishing(true);
        commitSetStatus({
            variables: { input: { productID: product.id, status: newStatusId === 1 ? 'PUBLISHED' : 'UN_PUBLISHED' } },
            onCompleted: () => {
                setIsPublishing(false);
            },
            onError: () => {
                setOptimisticStatusId(product.statusID);
                setIsPublishing(false);
            }
        });
    };

    const handleAssignPrintMethod = async (printMethodRelayId: string) => {
        commitAssignPrintMethod({
            variables: { input: { printTypeID: printMethodRelayId, modelSidesToAssignsDto: [{ modelID: product.id, sides: [] }] } },
            onCompleted: () => {
                setDataRefreshKey((k) => k + 1);
                onToolAssignmentChange?.();
            }
        });
    };

    const handleUnassignPrintMethod = (itemId: string) => {
        const methodName = (product.printingMethods || []).find((printMethod) => printMethod.id === itemId)?.name || itemId;
        showDialog(
            'unassign-print-method',
            <QuestionDialog
                title={T._('Warning', 'Admin')}
                message={T._(`Are you sure you want to unassign the printing method "${methodName}" from this product?`, 'Admin')}
                buttonYesLabel={T._('Yes', 'Admin')}
                buttonNoLabel={T._('No', 'Admin')}
                onYesClick={() => {
                    closeDialog('unassign-print-method');
                    setUnassigningItemId(itemId);
                    commitUnassignPrintMethod({
                        variables: { input: { printTypeID: itemId, productIDsToRemove: [product.id] } },
                        onCompleted: () => {
                            startUnassignTransition(() => {
                                setDataRefreshKey((k) => k + 1);
                            });
                            onToolAssignmentChange?.();
                        }
                    });
                }}
                onNoClick={() => closeDialog('unassign-print-method')}
            />
        );
    };

    const handleAssignPricingRule = async (pricingId: string, pricingName: string) => {
        commitAssignPricing({
            variables: { pricingId, productIds: [product.modelID] },
            onCompleted: () => {
                setDataRefreshKey((k) => k + 1);
                onToolAssignmentChange?.();
            }
        });
    };

    const handleUnassignPricingRule = async (pricingId: string) => {
        setUnassigningItemId(pricingId);
        commitRemovePricing({
            variables: { pricingId, productIds: [product.modelID] },
            onCompleted: () => {
                startUnassignTransition(() => {
                    setDataRefreshKey((k) => k + 1);
                });
                onToolAssignmentChange?.();
            },
            onError: () => {
                setUnassigningItemId(null);
            }
        });
    };

    const handleUnassignGenericTool = async (toolKey: string, itemId: string) => {
        setUnassigningItemId(itemId);
        const onDone = () => {
            startUnassignTransition(() => setDataRefreshKey((k) => k + 1));
            onToolAssignmentChange?.();
        };
        try {
            if (toolKey === 'moq') {
                commitRemoveMoq({ variables: { moqRuleId: itemId, productIds: [product.modelID] }, onCompleted: onDone });
            } else if (toolKey === 'ai-lifestyle-scenes') {
                commitRemoveAIStaging({ variables: { ruleId: itemId, productIds: [product.modelID] }, onCompleted: onDone });
            } else if (toolKey === 'ai-try-on') {
                commitRemoveAIVirtualModel({ variables: { ruleId: itemId, productIds: [product.modelID] }, onCompleted: onDone });
            } else if (toolKey === 'profanity') {
                commitRemoveProfanity({ variables: { profanityRuleId: itemId, productIds: [product.modelID] }, onCompleted: onDone });
            } else if (toolKey === 'collage') {
                commitRemoveGridRule({ variables: { gridRuleId: itemId, productIds: [product.modelID] }, onCompleted: onDone });
            } else if (toolKey === 'area-background') {
                commitRemoveAreaBg({ variables: { ruleId: itemId, productIds: [product.modelID] }, onCompleted: onDone });
            } else if (toolKey === 'get-a-quote') {
                commitRemoveQuoteRule({ variables: { ruleId: itemId, productIds: [product.modelID] }, onCompleted: onDone });
            }
            setDataRefreshKey((k) => k + 1);
        } catch {
            // ignore
        }
    };

    // ========== Render Content ==========

    const headerContent = (
        <PanelHeaderContent>
            <PanelHeaderImg>
                {product.smallImageUrl ? <img src={product.smallImageUrl} alt={product.name} /> : <ImagePlaceholder>N/A</ImagePlaceholder>}
            </PanelHeaderImg>
            <PanelHeaderInfo>
                <h2>{product.name}</h2>
                <HeaderMeta>
                    <span>{product.code}</span>
                </HeaderMeta>
            </PanelHeaderInfo>
        </PanelHeaderContent>
    );
    const fullScreenHeaderContent = (
        <PanelHeaderContent>
            <PanelHeaderImg>
                {product.smallImageUrl ? <img src={product.smallImageUrl} alt={product.name} /> : <ImagePlaceholder>N/A</ImagePlaceholder>}
            </PanelHeaderImg>
            <PanelHeaderInfo>
                <h2>{product.name}</h2>
                <HeaderMeta>
                    <span>{product.code}</span>
                    <HeaderSep>&middot;</HeaderSep>
                    <ToolBadge $type={product.zakekeEnvironment}>{getToolLabel(product.zakekeEnvironment)}</ToolBadge>
                    <StatusBadge $online={isOnline}>{isOnline ? T._('Online', 'Admin') : T._('Not published', 'Admin')}</StatusBadge>
                    {salesChannelCount > 0 && (
                        <>
                            <HeaderSep>&middot;</HeaderSep>
                            <span>
                                {salesChannelCount} {salesChannelCount === 1 ? T._('channel', 'Admin') : T._('channels', 'Admin')}
                            </span>
                        </>
                    )}
                </HeaderMeta>
            </PanelHeaderInfo>
        </PanelHeaderContent>
    );
    const footerContent = (
        <PanelFooter>
            <FooterButton $primary onClick={handleEditProduct}>
                {T._('Edit Product', 'Admin')}
            </FooterButton>
            {onPublishToggle && (
                <FooterButton $publish={!isOnline} $unpublish={isOnline} onClick={handlePublishToggle} disabled={isPublishing}>
                    {isPublishing ? '...' : isOnline ? T._('Unpublish', 'Admin') : T._('Publish', 'Admin')}
                </FooterButton>
            )}
            {onDuplicate && (
                <FooterButton
                    onClick={() => {
                        onClose();
                        onDuplicate();
                    }}
                >
                    {T._('Duplicate', 'Admin')}
                </FooterButton>
            )}
            {onDelete && (
                <FooterButton $danger onClick={() => setConfirmingDelete(true)}>
                    {T._('Delete', 'Admin')}
                </FooterButton>
            )}
        </PanelFooter>
    );
    return (
        <>
            <SidePanelDrawer
                isOpen={true}
                onClose={onClose}
                header={headerContent}
                headerActions={
                    onPreview ? (
                        <HeaderPreviewButton
                            onClick={() => {
                                onClose();
                                onPreview();
                            }}
                        >
                            {T._('Preview', 'Admin')}
                        </HeaderPreviewButton>
                    ) : undefined
                }
                showFullScreenButton
                fullScreenHeader={fullScreenHeaderContent}
                footer={footerContent}
                isDimmed={
                    !!stackedEditor ||
                    showAddPrintingMethod ||
                    showAddPricingRule ||
                    showAddCollageRule ||
                    showAddAreaBackgroundRule ||
                    showAddProfanityRule ||
                    showAddAIStagingRule ||
                    showAddAIVirtualModelRule ||
                    showAddMoqRule ||
                    showAddQuoteRule
                }
                width={580}
                overlayBackground='rgba(0, 0, 0, 0.08)'
                skipAnimation
            >
                {({ isFullScreen }) =>
                    isFullScreen ? (
                        <OverviewBody>
                            <OverviewGrid>
                                {/* Product structure / Configurator card */}
                                {product.zakekeEnvironment === 'COMPOSER' ? (
                                    <>
                                        <OverviewCategoryTitle>{T._('Scene', 'Admin')}</OverviewCategoryTitle>
                                        <OverviewCard>
                                            <OverviewCardHeader>
                                                <OverviewCardTitle>{T._('Meshes', 'Admin')}</OverviewCardTitle>
                                                <OverviewCardBadge $active={(configuratorData?.sceneMeshes.length || 0) > 0}>
                                                    {configuratorData?.sceneMeshes.length || 0}
                                                </OverviewCardBadge>
                                            </OverviewCardHeader>
                                            {configuratorData?.sceneMeshes.map((mesh, i) => (
                                                <OverviewCardItem key={i}>
                                                    <ItemDot $color='#3b82f6' />
                                                    <span>
                                                        {mesh.meshID.substring(0, 8)} ({mesh.sides.length}{' '}
                                                        {mesh.sides.length === 1 ? T._('side', 'Admin') : T._('sides', 'Admin')}
                                                        {mesh.materials.length > 0 &&
                                                            `, ${mesh.materials.length} ${mesh.materials.length === 1 ? T._('material', 'Admin') : T._('materials', 'Admin')}`}
                                                        )
                                                    </span>
                                                </OverviewCardItem>
                                            ))}
                                        </OverviewCard>
                                        <OverviewCategoryTitle>{T._('Composer', 'Admin')}</OverviewCategoryTitle>
                                        <OverviewCard>
                                            <OverviewCardHeader>
                                                <OverviewCardTitle>{T._('Attributes', 'Admin')}</OverviewCardTitle>
                                                <OverviewCardBadge $active={(configuratorData?.attributes.length || 0) > 0}>
                                                    {configuratorData?.attributes.length || 0}
                                                </OverviewCardBadge>
                                            </OverviewCardHeader>
                                            {configuratorData?.attributes.map((attr, i) => (
                                                <OverviewCardItem key={i}>
                                                    <ItemDot $color='#3b82f6' />
                                                    <span>
                                                        {attr.name} ({attr.options.length} {T._('options', 'Admin')}
                                                        {attr.actionsCount > 0 ? `, ${attr.actionsCount} ${T._('actions', 'Admin')}` : ''})
                                                    </span>
                                                </OverviewCardItem>
                                            ))}
                                        </OverviewCard>
                                    </>
                                ) : (
                                    <>
                                        <OverviewCategoryTitle>{T._('Product Structure', 'Admin')}</OverviewCategoryTitle>
                                        <OverviewCard>
                                            <OverviewCardHeader>
                                                <OverviewCardTitle>{T._('Product structure', 'Admin')}</OverviewCardTitle>
                                            </OverviewCardHeader>
                                            <StructureSummaryGrid>
                                                <StructureSummaryCard>
                                                    <StructureSummaryValue>{productStructureSummary.totalVariants}</StructureSummaryValue>
                                                    <StructureSummaryLabel>
                                                        {productStructureSummary.totalVariants === 1 ? T._('Variant', 'Admin') : T._('Variants', 'Admin')}
                                                    </StructureSummaryLabel>
                                                </StructureSummaryCard>
                                                <StructureSummaryCard>
                                                    <StructureSummaryValue>{productStructureSummary.maxSides}</StructureSummaryValue>
                                                    <StructureSummaryLabel>
                                                        {productStructureSummary.maxSides === 1 ? T._('Side', 'Admin') : T._('Sides', 'Admin')}
                                                    </StructureSummaryLabel>
                                                </StructureSummaryCard>
                                                <StructureSummaryCard>
                                                    <StructureSummaryValue>{productStructureSummary.maxPrintAreas}</StructureSummaryValue>
                                                    <StructureSummaryLabel>
                                                        {productStructureSummary.maxPrintAreas === 1 ? T._('Print Area', 'Admin') : T._('Print Areas', 'Admin')}
                                                    </StructureSummaryLabel>
                                                </StructureSummaryCard>
                                            </StructureSummaryGrid>
                                        </OverviewCard>
                                    </>
                                )}
                                {/* Tool categories */}
                                {TOOL_CATEGORY_ORDER.map((category) => {
                                    const categoryTools = availableTools.filter((t) => t.category === category);
                                    if (categoryTools.length === 0) return null;
                                    return (
                                        <React.Fragment key={category}>
                                            <OverviewCategoryTitle>{TOOL_CATEGORY_LABELS[category]}</OverviewCategoryTitle>
                                            {categoryTools.map((tool) => {
                                                const info = getToolInfo(product, tool.key, toolData);
                                                return (
                                                    <OverviewCard key={tool.key}>
                                                        <OverviewCardHeader>
                                                            <OverviewCardTitle>{tool.name}</OverviewCardTitle>
                                                            <OverviewCardBadge $active={info.count > 0}>{info.count > 0 ? info.count : '--'}</OverviewCardBadge>
                                                        </OverviewCardHeader>
                                                        {!toolData.loaded ? (
                                                            <OverviewCardEmpty>
                                                                <ContentLoader
                                                                    speed={2}
                                                                    width='100%'
                                                                    height='14'
                                                                    backgroundColor={theme.skeleton.backgroundColor}
                                                                    foregroundColor={theme.skeleton.foregroundColor}
                                                                >
                                                                    <rect x='0' y='0' rx='4' ry='4' width='80%' height='14' />
                                                                </ContentLoader>
                                                            </OverviewCardEmpty>
                                                        ) : info.items.length > 0 ? (
                                                            info.items.slice(0, 10).map((item) => (
                                                                <OverviewCardItem key={item.id}>
                                                                    <ItemDot $color='#3b82f6' />
                                                                    <span>{item.name}</span>
                                                                </OverviewCardItem>
                                                            ))
                                                        ) : info.status ? (
                                                            <OverviewCardItem>
                                                                <ItemDot $color='#10b981' />
                                                                <span>{info.status}</span>
                                                            </OverviewCardItem>
                                                        ) : (
                                                            <OverviewCardEmpty>{T._('Not configured', 'Admin')}</OverviewCardEmpty>
                                                        )}
                                                        {info.items.length > 10 && (
                                                            <OverviewCardItem>
                                                                <ItemMeta>
                                                                    +{info.items.length - 10} {T._('more', 'Admin')}
                                                                </ItemMeta>
                                                            </OverviewCardItem>
                                                        )}
                                                    </OverviewCard>
                                                );
                                            })}
                                        </React.Fragment>
                                    );
                                })}
                            </OverviewGrid>
                        </OverviewBody>
                    ) : (
                        <SidePanelScrollArea>
                            {/* Status bar: tool + status + sales channels */}
                            <StatusBar>
                                <ToolBadge $type={product.zakekeEnvironment}>{getToolLabel(product.zakekeEnvironment)}</ToolBadge>
                                <StatusBadge $online={isOnline}>{isOnline ? T._('Online', 'Admin') : T._('Not published', 'Admin')}</StatusBadge>
                                <StatusBarSpacer />
                                <ChannelsBadge $count={salesChannelCount} onClick={() => setChannelsOpen(!channelsOpen)}>
                                    {salesChannelCount > 0
                                        ? salesChannelCount + ' ' + (salesChannelCount === 1 ? T._('channel', 'Admin') : T._('channels', 'Admin'))
                                        : T._('No channels', 'Admin')}
                                    {salesChannelCount > 0 && <span>{channelsOpen ? '▴' : '▾'}</span>}
                                </ChannelsBadge>
                            </StatusBar>
                            {/* Channels dropdown */}
                            {channelsOpen && (
                                <ChannelsDropdown>
                                    {salesChannels.map((salesChannel, channelIndex) => (
                                        <ChannelItem key={channelIndex}>
                                            <ChannelDot />
                                            <span>{salesChannel.name}</span>
                                            <ChannelMeta>{salesChannel.ecommerce}</ChannelMeta>
                                        </ChannelItem>
                                    ))}
                                    {onAddSalesChannel && (
                                        <AddChannelButton onClick={() => onAddSalesChannel()}>+ {T._('Add to a Sales Channel', 'Admin')}</AddChannelButton>
                                    )}
                                </ChannelsDropdown>
                            )}
                            {/* ===== PRODUCT STRUCTURE / CONFIGURATOR ===== */}
                            {product.zakekeEnvironment === 'COMPOSER' ? (
                                <>
                                    {/* ---- COMPOSER ---- */}
                                    <StructureSection>
                                        <StructureSectionHeader>
                                            <StructureSectionTitle>
                                                {T._('Attributes & Options', 'Admin')}
                                                <InfoIcon
                                                    href='https://zakeke.zendesk.com/hc/en-us/articles/360009576134-Composer'
                                                    target='_blank'
                                                    rel='noopener'
                                                    title={T._('Guide', 'Admin')}
                                                >
                                                    i
                                                </InfoIcon>
                                            </StructureSectionTitle>
                                            {configuratorData && (
                                                <StructureTreeToggle onClick={() => setShowComposerTree(!showComposerTree)}>
                                                    {showComposerTree ? '▴' : '▾'} {T._('Details', 'Admin')}
                                                </StructureTreeToggle>
                                            )}
                                        </StructureSectionHeader>
                                        {!configuratorData ? (
                                            <InlineSkeleton>
                                                <ContentLoader
                                                    speed={2}
                                                    width='100%'
                                                    height='40'
                                                    backgroundColor={theme.skeleton.backgroundColor}
                                                    foregroundColor={theme.skeleton.foregroundColor}
                                                >
                                                    <rect x='0' y='0' rx='4' ry='4' width='100%' height='14' />
                                                    <rect x='0' y='22' rx='4' ry='4' width='70%' height='14' />
                                                </ContentLoader>
                                            </InlineSkeleton>
                                        ) : (
                                            <>
                                                <StructureTransitionWrapper>
                                                    <StructureTransitionPanel $visible={!showComposerTree}>
                                                        <StructureSummaryGrid>
                                                            <StructureSummaryCard>
                                                                <StructureSummaryValue>{configuratorData.attributes.length}</StructureSummaryValue>
                                                                <StructureSummaryLabel>
                                                                    {configuratorData.attributes.length === 1
                                                                        ? T._('Attribute', 'Admin')
                                                                        : T._('Attributes', 'Admin')}
                                                                </StructureSummaryLabel>
                                                            </StructureSummaryCard>
                                                            <StructureSummaryCard>
                                                                <StructureSummaryValue>
                                                                    {configuratorData.attributes.reduce((sum, attr) => sum + attr.options.length, 0)}
                                                                </StructureSummaryValue>
                                                                <StructureSummaryLabel>{T._('Options', 'Admin')}</StructureSummaryLabel>
                                                            </StructureSummaryCard>
                                                            <StructureSummaryCard>
                                                                <StructureSummaryValue>
                                                                    {configuratorData.attributes.reduce((sum, attr) => sum + attr.actionsCount, 0)}
                                                                </StructureSummaryValue>
                                                                <StructureSummaryLabel>{T._('Actions', 'Admin')}</StructureSummaryLabel>
                                                            </StructureSummaryCard>
                                                        </StructureSummaryGrid>
                                                    </StructureTransitionPanel>
                                                    <StructureTransitionPanel $visible={showComposerTree}>
                                                        <StructureTree>
                                                            {configuratorData.attributes.map((attr, i) => (
                                                                <TreeVariant key={i}>
                                                                    <TreeVariantHeader
                                                                        $clickable
                                                                        onClick={() => {
                                                                            setStackedEditor({
                                                                                type: 'composer-attribute',
                                                                                name: attr.name,
                                                                                id: '',
                                                                                filterMode: 'attribute'
                                                                            });
                                                                        }}
                                                                    >
                                                                        <TreeIcon>&#9724;</TreeIcon>
                                                                        <span>{attr.name}</span>
                                                                        <ItemMeta>
                                                                            {attr.options.length}{' '}
                                                                            {attr.options.length === 1 ? T._('option', 'Admin') : T._('options', 'Admin')}
                                                                            {attr.actionsCount > 0
                                                                                ? `, ${attr.actionsCount} ${attr.actionsCount === 1 ? T._('action', 'Admin') : T._('actions', 'Admin')}`
                                                                                : ''}
                                                                        </ItemMeta>
                                                                        <ItemAction>{T._('Edit', 'Admin')} &#8250;</ItemAction>
                                                                    </TreeVariantHeader>
                                                                    {attr.options.map((opt, oIdx) => (
                                                                        <TreeSide
                                                                            key={oIdx}
                                                                            $clickable
                                                                            onClick={() => {
                                                                                setStackedEditor({
                                                                                    type: 'composer-attribute',
                                                                                    name: attr.name,
                                                                                    id: '',
                                                                                    filterMode: 'option',
                                                                                    optionName: opt.name
                                                                                });
                                                                            }}
                                                                        >
                                                                            <TreeIcon>&#9679;</TreeIcon>
                                                                            <span>{opt.name}</span>
                                                                            {opt.price != null && opt.price !== 0 && (
                                                                                <ItemMetaPushed>
                                                                                    {opt.price > 0 ? '+' : ''}
                                                                                    {opt.price}
                                                                                </ItemMetaPushed>
                                                                            )}
                                                                            {opt.hasLink && (
                                                                                <ConditionalBadge title={T._('Conditional rule', 'Admin')}>
                                                                                    &#8645;
                                                                                </ConditionalBadge>
                                                                            )}
                                                                        </TreeSide>
                                                                    ))}
                                                                </TreeVariant>
                                                            ))}
                                                        </StructureTree>
                                                        <TreeManageLink>
                                                            <a href={`/Composer?id=${product.modelID}&page=product&subpage=composer`}>
                                                                {T._('Manage', 'Admin')}
                                                            </a>
                                                        </TreeManageLink>
                                                    </StructureTransitionPanel>
                                                </StructureTransitionWrapper>
                                            </>
                                        )}
                                    </StructureSection>
                                    {/* ---- CUSTOMIZER ---- */}
                                    <StructureSection>
                                        <StructureSectionHeader>
                                            <StructureSectionTitle>
                                                {T._('Personalization', 'Admin')}
                                                <InfoIcon
                                                    href='https://zakeke.zendesk.com/hc/en-us/articles/10734802189340-Pre-designed-templates-for-Configurable-products'
                                                    target='_blank'
                                                    rel='noopener'
                                                    title={T._('Guide', 'Admin')}
                                                >
                                                    i
                                                </InfoIcon>
                                            </StructureSectionTitle>
                                        </StructureSectionHeader>
                                        {!configuratorData ? (
                                            <InlineSkeleton>
                                                <ContentLoader
                                                    speed={2}
                                                    width='100%'
                                                    height='40'
                                                    backgroundColor={theme.skeleton.backgroundColor}
                                                    foregroundColor={theme.skeleton.foregroundColor}
                                                >
                                                    <rect x='0' y='0' rx='4' ry='4' width='100%' height='14' />
                                                    <rect x='0' y='22' rx='4' ry='4' width='70%' height='14' />
                                                </ContentLoader>
                                            </InlineSkeleton>
                                        ) : (
                                            <>
                                                <StructureSummaryGrid>
                                                    <StructureSummaryCard>
                                                        <StructureSummaryValue>
                                                            {configuratorData.sceneMeshes.reduce((sum, mesh) => sum + mesh.sides.length, 0)}
                                                        </StructureSummaryValue>
                                                        <StructureSummaryLabel>
                                                            {configuratorData.sceneMeshes.reduce((sum, mesh) => sum + mesh.sides.length, 0) === 1
                                                                ? T._('Customizable Area', 'Admin')
                                                                : T._('Customizable Areas', 'Admin')}
                                                        </StructureSummaryLabel>
                                                    </StructureSummaryCard>
                                                </StructureSummaryGrid>
                                                {availableTools
                                                    .filter((t) => {
                                                        if (!t.customizerTool) return false;
                                                        // Hide pricing rules for configurator products with no customizable areas
                                                        if (
                                                            t.key === 'pricing' &&
                                                            product.zakekeEnvironment === 'COMPOSER' &&
                                                            configuratorData &&
                                                            configuratorData.sceneMeshes.reduce((sum, mesh) => sum + mesh.sides.length, 0) === 0
                                                        )
                                                            return false;
                                                        return true;
                                                    })
                                                    .map((tool) => {
                                                        const info = getToolInfo(product, tool.key, toolData);
                                                        const isPricing = tool.key === 'pricing';
                                                        const assignedPricingIds = isPricing
                                                            ? new Set((toolData.pricingRules || []).map((r) => r.id))
                                                            : new Set<string>();
                                                        const hasAssignablePricingRules =
                                                            isPricing &&
                                                            accountPricingRules !== null &&
                                                            accountPricingRules.some((pricingRule) => !assignedPricingIds.has(pricingRule.id));
                                                        const accountHasCustomPricingRules =
                                                            isPricing && accountPricingRules !== null && accountPricingRules.length > 0;
                                                        const isComposerPricing = isPricing && product.zakekeEnvironment === 'COMPOSER';
                                                        const handleAddClick = () => {
                                                            if (isPricing && hasAssignablePricingRules) {
                                                                setShowAddPricingRule(true);
                                                            } else if (isPricing) {
                                                                navigate(backofficeUrl('/pricing-advanced'), { state: productState });
                                                            } else if (product.zakekeEnvironment === 'COMPOSER') {
                                                                handleGoToComposer();
                                                            } else if (tool.buildSetupRoute) {
                                                                navigate(backofficeUrl(tool.buildSetupRoute(product.modelID.toString())), {
                                                                    state: productState
                                                                });
                                                            } else if (tool.newRoute) {
                                                                handleGoToFullTool(tool.newRoute);
                                                            } else {
                                                                handleGoToFullTool(tool.route);
                                                            }
                                                        };
                                                        const hasItems = info.items.length > 0;
                                                        const canAddMore = tool.newRoute || tool.buildSetupRoute || isPricing;
                                                        return (
                                                            <CollapsibleSection
                                                                key={`${tool.key}-${expandAllKey}`}
                                                                title={tool.name}
                                                                count={info.count}
                                                                badgeLabel={BOOLEAN_TOOLS.includes(tool.key) ? (info.count > 0 ? '✓' : '—') : undefined}
                                                                badgeBg={info.count > 0 ? '#dbeafe' : '#f3f4f6'}
                                                                badgeFg={info.count > 0 ? '#1e40af' : '#6b7280'}
                                                                helpUrl={tool.helpUrl}
                                                                onOpenChange={(isOpen) => {
                                                                    setOpenSections((prev) => {
                                                                        const next = new Set(prev);
                                                                        if (isOpen) next.add(tool.key);
                                                                        else next.delete(tool.key);
                                                                        return next;
                                                                    });
                                                                }}
                                                            >
                                                                {!toolData.loaded ? (
                                                                    <ContentLoader
                                                                        speed={2}
                                                                        width='100%'
                                                                        height='40'
                                                                        backgroundColor={theme.skeleton.backgroundColor}
                                                                        foregroundColor={theme.skeleton.foregroundColor}
                                                                    >
                                                                        <rect x='0' y='0' rx='4' ry='4' width='100%' height='14' />
                                                                        <rect x='0' y='22' rx='4' ry='4' width='70%' height='14' />
                                                                    </ContentLoader>
                                                                ) : hasItems ? (
                                                                    <>
                                                                        {info.items.slice(0, 10).map((item) => (
                                                                            <PanelItem
                                                                                key={item.id}
                                                                                $clickable
                                                                                onClick={() => handleOpenToolEditor(tool.key, item.id)}
                                                                            >
                                                                                {item.previewUrl ? (
                                                                                    <ItemPreviewImg src={item.previewUrl} alt={item.name} />
                                                                                ) : (
                                                                                    <ItemDot $color='#3b82f6' />
                                                                                )}
                                                                                <ItemName>{item.name}</ItemName>
                                                                                {isPricing && (
                                                                                    <ItemActionDanger
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleUnassignPricingRule(item.id);
                                                                                        }}
                                                                                    >
                                                                                        {T._('Unassign', 'Admin')}
                                                                                    </ItemActionDanger>
                                                                                )}
                                                                                <ItemAction>{T._('Edit', 'Admin')} &#8250;</ItemAction>
                                                                            </PanelItem>
                                                                        ))}
                                                                        {info.items.length > 10 && (
                                                                            <PanelItem>
                                                                                <ItemMeta>
                                                                                    +{info.items.length - 10} {T._('more', 'Admin')}
                                                                                </ItemMeta>
                                                                            </PanelItem>
                                                                        )}
                                                                        {canAddMore && (
                                                                            <PanelItemSpaced $clickable onClick={handleAddClick}>
                                                                                <ItemDot $color='#d1d5db' />
                                                                                <ItemNameAction>+ {T._('Add', 'Admin')}</ItemNameAction>
                                                                            </PanelItemSpaced>
                                                                        )}
                                                                    </>
                                                                ) : info.status ? (
                                                                    <PanelItem
                                                                        $clickable
                                                                        onClick={() => {
                                                                            if (product.zakekeEnvironment === 'COMPOSER') {
                                                                                handleGoToComposer();
                                                                            } else if (tool.key === 'placement' && tool.buildSetupRoute) {
                                                                                navigate(backofficeUrl(tool.buildSetupRoute(product.modelID.toString())), {
                                                                                    state: productState
                                                                                });
                                                                            } else if (tool.buildEmbeddedUrl) {
                                                                                handleOpenToolEditor(tool.key, '');
                                                                            } else if (tool.buildSetupRoute) {
                                                                                navigate(backofficeUrl(tool.buildSetupRoute(product.modelID.toString())), {
                                                                                    state: productState
                                                                                });
                                                                            } else {
                                                                                handleGoToFullTool(tool.route);
                                                                            }
                                                                        }}
                                                                    >
                                                                        <ItemDot $color='#10b981' />
                                                                        <ItemName>{info.status}</ItemName>
                                                                        <ItemAction>{T._('Manage', 'Admin')} &#8250;</ItemAction>
                                                                    </PanelItem>
                                                                ) : (
                                                                    <>
                                                                        <EmptyState>
                                                                            {isComposerPricing ? (
                                                                                <>
                                                                                    {T._('Create price markup for the design part of your product.', 'Admin')}{' '}
                                                                                    {T._('For pricing the configuration part, follow', 'Admin')}{' '}
                                                                                    <LearnMoreLink
                                                                                        href='https://zakeke.zendesk.com/hc/en-us/articles/360009576134-Composer#h_01G0M6P43K3A6F8NHZBFVHP02J'
                                                                                        target='_blank'
                                                                                        rel='noopener'
                                                                                    >
                                                                                        {T._('this guide', 'Admin')} &#8250;
                                                                                    </LearnMoreLink>
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    {tool.description}
                                                                                    {tool.helpUrl && (
                                                                                        <>
                                                                                            {' — '}
                                                                                            <LearnMoreLink href={tool.helpUrl} target='_blank' rel='noopener'>
                                                                                                {T._('Learn more', 'Admin')} &#8250;
                                                                                            </LearnMoreLink>
                                                                                        </>
                                                                                    )}
                                                                                </>
                                                                            )}
                                                                        </EmptyState>
                                                                        <PanelItemSpacedLarge $clickable onClick={handleAddClick}>
                                                                            <ItemDot $color='#d1d5db' />
                                                                            <ItemNameAction>
                                                                                + {accountHasCustomPricingRules ? T._('Add', 'Admin') : T._('Set up', 'Admin')}
                                                                            </ItemNameAction>
                                                                        </PanelItemSpacedLarge>
                                                                    </>
                                                                )}
                                                            </CollapsibleSection>
                                                        );
                                                    })}
                                            </>
                                        )}
                                    </StructureSection>
                                    {/* ---- TOOLS ---- */}
                                    <PanelBody>
                                        <FilterBar>
                                            {openSections.size >= 3 && (
                                                <FilterBarLink
                                                    onClick={() => {
                                                        setOpenSections(new Set());
                                                        setExpandAllKey((k) => k + 1);
                                                    }}
                                                >
                                                    {T._('Collapse all', 'Admin')}
                                                </FilterBarLink>
                                            )}
                                            <Checkbox
                                                label={T._('Hide unconfigured tools', 'Admin')}
                                                checked={hideUnconfigured}
                                                onChange={(checked) => {
                                                    setHideUnconfigured(checked);
                                                    try {
                                                        localStorage.setItem('zakeke_hideUnconfiguredTools', String(checked));
                                                    } catch {
                                                        /* */
                                                    }
                                                }}
                                            />
                                        </FilterBar>
                                        <GroupLabel>{T._('Tools', 'Admin')}</GroupLabel>
                                        {availableTools
                                            .filter((t) => {
                                                if (t.customizerTool) return false;
                                                if (!hideUnconfigured) return true;
                                                const info = getToolInfo(product, t.key, toolData);
                                                return info.count > 0 || !!info.status;
                                            })
                                            .map((tool) => {
                                                const info = getToolInfo(product, tool.key, toolData);
                                                const isPricing = tool.key === 'pricing';
                                                const handleAddClick = () => {
                                                    if (
                                                        isPricing &&
                                                        accountPricingRules !== null &&
                                                        accountPricingRules.some(
                                                            (pricingRule) => !new Set((toolData.pricingRules || []).map((rule) => rule.id)).has(pricingRule.id)
                                                        )
                                                    ) {
                                                        setShowAddPricingRule(true);
                                                    } else if (isPricing) {
                                                        navigate(backofficeUrl('/pricing-advanced'), { state: productState });
                                                    } else {
                                                        handleGoToComposer();
                                                    }
                                                };
                                                const hasItems = info.items.length > 0;
                                                const canAddMore = tool.newRoute || tool.buildSetupRoute || isPricing;
                                                return (
                                                    <CollapsibleSection
                                                        key={`${tool.key}-${expandAllKey}`}
                                                        title={tool.name}
                                                        count={info.count}
                                                        badgeLabel={BOOLEAN_TOOLS.includes(tool.key) ? (info.count > 0 ? '✓' : '—') : undefined}
                                                        badgeBg={info.count > 0 ? '#dbeafe' : '#f3f4f6'}
                                                        badgeFg={info.count > 0 ? '#1e40af' : '#6b7280'}
                                                        helpUrl={tool.helpUrl}
                                                        onOpenChange={(isOpen) => {
                                                            setOpenSections((prev) => {
                                                                const next = new Set(prev);
                                                                if (isOpen) next.add(tool.key);
                                                                else next.delete(tool.key);
                                                                return next;
                                                            });
                                                        }}
                                                    >
                                                        {!toolData.loaded ? (
                                                            <ContentLoader
                                                                speed={2}
                                                                width='100%'
                                                                height='40'
                                                                backgroundColor={theme.skeleton.backgroundColor}
                                                                foregroundColor={theme.skeleton.foregroundColor}
                                                            >
                                                                <rect x='0' y='0' rx='4' ry='4' width='100%' height='14' />
                                                                <rect x='0' y='22' rx='4' ry='4' width='70%' height='14' />
                                                            </ContentLoader>
                                                        ) : hasItems ? (
                                                            <>
                                                                {info.items.slice(0, 10).map((item) => (
                                                                    <PanelItem key={item.id} $clickable onClick={() => handleOpenToolEditor(tool.key, item.id)}>
                                                                        <ItemDot $color='#3b82f6' />
                                                                        <ItemName>{item.name}</ItemName>
                                                                        <ItemAction>{T._('Edit', 'Admin')} &#8250;</ItemAction>
                                                                    </PanelItem>
                                                                ))}
                                                                {info.items.length > 10 && (
                                                                    <PanelItem>
                                                                        <ItemMeta>
                                                                            +{info.items.length - 10} {T._('more', 'Admin')}
                                                                        </ItemMeta>
                                                                    </PanelItem>
                                                                )}
                                                                {canAddMore && (
                                                                    <PanelItemSpaced $clickable onClick={handleAddClick}>
                                                                        <ItemDot $color='#d1d5db' />
                                                                        <ItemNameAction>+ {T._('Add', 'Admin')}</ItemNameAction>
                                                                    </PanelItemSpaced>
                                                                )}
                                                            </>
                                                        ) : info.status ? (
                                                            <PanelItem
                                                                $clickable
                                                                onClick={() => {
                                                                    if (tool.buildEmbeddedUrl) {
                                                                        handleOpenToolEditor(tool.key, '');
                                                                    } else {
                                                                        handleGoToComposer();
                                                                    }
                                                                }}
                                                            >
                                                                <ItemDot $color='#10b981' />
                                                                <ItemName>{info.status}</ItemName>
                                                                <ItemAction>{T._('Manage', 'Admin')} &#8250;</ItemAction>
                                                            </PanelItem>
                                                        ) : (
                                                            <>
                                                                <EmptyState>
                                                                    {tool.description}
                                                                    {tool.helpUrl && (
                                                                        <>
                                                                            {' — '}
                                                                            <LearnMoreLink href={tool.helpUrl} target='_blank' rel='noopener'>
                                                                                {T._('Learn more', 'Admin')} &#8250;
                                                                            </LearnMoreLink>
                                                                        </>
                                                                    )}
                                                                </EmptyState>
                                                                <PanelItemSpacedLarge $clickable onClick={handleAddClick}>
                                                                    <ItemDot $color='#d1d5db' />
                                                                    <ItemNameAction>+ {T._('Set up', 'Admin')}</ItemNameAction>
                                                                </PanelItemSpacedLarge>
                                                            </>
                                                        )}
                                                    </CollapsibleSection>
                                                );
                                            })}
                                    </PanelBody>
                                </>
                            ) : (
                                <StructureSection>
                                    <StructureSectionHeader>
                                        <StructureSectionTitle>{T._('Product Structure', 'Admin')}</StructureSectionTitle>
                                        <StructureTreeToggle onClick={() => setShowStructureTree(!showStructureTree)}>
                                            {showStructureTree ? '▴' : '▾'} {T._('Details', 'Admin')}
                                        </StructureTreeToggle>
                                    </StructureSectionHeader>
                                    <StructureTransitionWrapper>
                                        <StructureTransitionPanel $visible={!showStructureTree}>
                                            <StructureSummaryGrid>
                                                <StructureSummaryCard>
                                                    <StructureSummaryValue>{productStructureSummary.totalVariants}</StructureSummaryValue>
                                                    <StructureSummaryLabel>
                                                        {productStructureSummary.totalVariants === 1 ? T._('Variant', 'Admin') : T._('Variants', 'Admin')}
                                                    </StructureSummaryLabel>
                                                </StructureSummaryCard>
                                                <StructureSummaryCard>
                                                    <StructureSummaryValue>{productStructureSummary.maxSides}</StructureSummaryValue>
                                                    <StructureSummaryLabel>
                                                        {productStructureSummary.maxSides === 1 ? T._('Side', 'Admin') : T._('Sides', 'Admin')}
                                                    </StructureSummaryLabel>
                                                </StructureSummaryCard>
                                                <StructureSummaryCard>
                                                    <StructureSummaryValue>{productStructureSummary.maxPrintAreas}</StructureSummaryValue>
                                                    <StructureSummaryLabel>
                                                        {productStructureSummary.maxPrintAreas === 1 ? T._('Print Area', 'Admin') : T._('Print Areas', 'Admin')}
                                                    </StructureSummaryLabel>
                                                </StructureSummaryCard>
                                            </StructureSummaryGrid>
                                        </StructureTransitionPanel>
                                        <StructureTransitionPanel $visible={showStructureTree}>
                                            <StructureTree>
                                                {(() => {
                                                    const treeColors = productColors.length > 0 ? productColors : allProductColors;
                                                    const isNoVariants = productColors.length === 0;
                                                    if (isNoVariants && treeColors.length === 1) {
                                                        const sides = treeColors[0].sides || [];
                                                        return sides.map((side, sIdx) => {
                                                            const areas = side.areas || [];
                                                            return (
                                                                <React.Fragment key={sIdx}>
                                                                    <TreeSideFlush>
                                                                        <TreeIcon>&#9724;</TreeIcon>
                                                                        <span>{side.name || `${T._('Side', 'Admin')} ${sIdx + 1}`}</span>
                                                                    </TreeSideFlush>
                                                                    {areas.map((area, aIdx) => (
                                                                        <TreePrintArea key={aIdx}>
                                                                            <TreeIcon>&#9679;</TreeIcon>
                                                                            <span>{area.name || `${T._('Print Area', 'Admin')} ${aIdx + 1}`}</span>
                                                                        </TreePrintArea>
                                                                    ))}
                                                                </React.Fragment>
                                                            );
                                                        });
                                                    }
                                                    return treeColors.map((color) => {
                                                        const isExpanded = expandedVariants.has(color.colorID);
                                                        const sides = color.sides || [];
                                                        const variantDomId = `variant-${color.colorID}`;
                                                        const toggleExpand = () => {
                                                            const willExpand = !expandedVariants.has(color.colorID);
                                                            setExpandedVariants((prev) => {
                                                                const next = new Set(prev);
                                                                if (willExpand) next.add(color.colorID);
                                                                else next.delete(color.colorID);
                                                                return next;
                                                            });
                                                            if (willExpand) {
                                                                setTimeout(() => {
                                                                    document
                                                                        .getElementById(variantDomId)
                                                                        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                                                }, 80);
                                                            }
                                                        };
                                                        return (
                                                            <TreeVariant key={color.colorID} id={variantDomId}>
                                                                <TreeVariantHeader
                                                                    $clickable={sides.length > 0}
                                                                    onClick={sides.length > 0 ? toggleExpand : undefined}
                                                                >
                                                                    <TreeIcon>{sides.length > 0 ? (isExpanded ? '▾' : '▸') : '–'}</TreeIcon>
                                                                    <span>{color.name}</span>
                                                                    <ItemMeta>
                                                                        {sides.length} {sides.length === 1 ? T._('side', 'Admin') : T._('sides', 'Admin')}
                                                                    </ItemMeta>
                                                                </TreeVariantHeader>
                                                                {isExpanded &&
                                                                    sides.map((side, sIdx) => {
                                                                        const areas = side.areas || [];
                                                                        return (
                                                                            <React.Fragment key={sIdx}>
                                                                                <TreeSide>
                                                                                    <TreeIcon>&#9724;</TreeIcon>
                                                                                    <span>{side.name || `${T._('Side', 'Admin')} ${sIdx + 1}`}</span>
                                                                                </TreeSide>
                                                                                {areas.map((area, aIdx) => (
                                                                                    <TreePrintArea key={aIdx}>
                                                                                        <TreeIcon>&#9679;</TreeIcon>
                                                                                        <span>{area.name || `${T._('Print Area', 'Admin')} ${aIdx + 1}`}</span>
                                                                                    </TreePrintArea>
                                                                                ))}
                                                                            </React.Fragment>
                                                                        );
                                                                    })}
                                                            </TreeVariant>
                                                        );
                                                    });
                                                })()}
                                            </StructureTree>
                                            <TreeManageLink>
                                                <span
                                                    onClick={() =>
                                                        handleGoToFullTool(
                                                            productColors.length === 0
                                                                ? '/product/print-area/?product_id=' + product.modelID
                                                                : '/product/variations/?product_id=' + product.modelID
                                                        )
                                                    }
                                                >
                                                    {T._('Manage product', 'Admin')}
                                                </span>
                                            </TreeManageLink>
                                        </StructureTransitionPanel>
                                    </StructureTransitionWrapper>
                                </StructureSection>
                            )}
                            {product.zakekeEnvironment !== 'COMPOSER' && (
                                <PanelBody>
                                    <FilterBar>
                                        {openSections.size >= 3 && (
                                            <FilterBarLink
                                                onClick={() => {
                                                    setOpenSections(new Set());
                                                    setExpandAllKey((k) => k + 1);
                                                }}
                                            >
                                                {T._('Collapse all', 'Admin')}
                                            </FilterBarLink>
                                        )}
                                        <Checkbox
                                            label={T._('Hide unconfigured tools', 'Admin')}
                                            checked={hideUnconfigured}
                                            onChange={(checked) => {
                                                setHideUnconfigured(checked);
                                                try {
                                                    localStorage.setItem('zakeke_hideUnconfiguredTools', String(checked));
                                                } catch {
                                                    /* */
                                                }
                                            }}
                                        />
                                    </FilterBar>
                                    {/* ===== TOOL CATEGORIES ===== */}
                                    {TOOL_CATEGORY_ORDER.map((category) => {
                                        const categoryTools = availableTools.filter((t) => {
                                            if (t.category !== category) return false;
                                            if (!hideUnconfigured) return true;
                                            const info = getToolInfo(product, t.key, toolData);
                                            return info.count > 0 || !!info.status;
                                        });
                                        if (categoryTools.length === 0) return null;
                                        return (
                                            <React.Fragment key={category}>
                                                <GroupLabel>{TOOL_CATEGORY_LABELS[category]}</GroupLabel>
                                                {categoryTools.map((tool) => {
                                                    const info = getToolInfo(product, tool.key, toolData);
                                                    // Ad-hoc rendering for pre-designed templates
                                                    if (tool.key === 'templates') {
                                                        return (
                                                            <CollapsibleSection
                                                                key={`${tool.key}-${expandAllKey}`}
                                                                title={tool.name}
                                                                count={info.count}
                                                                badgeBg={info.count > 0 ? '#dbeafe' : '#f3f4f6'}
                                                                badgeFg={info.count > 0 ? '#1e40af' : '#6b7280'}
                                                                helpUrl={tool.helpUrl}
                                                                onOpenChange={(isOpen) => {
                                                                    setOpenSections((prev) => {
                                                                        const next = new Set(prev);
                                                                        if (isOpen) next.add(tool.key);
                                                                        else next.delete(tool.key);
                                                                        return next;
                                                                    });
                                                                }}
                                                            >
                                                                {!toolData.loaded ? (
                                                                    <ContentLoader
                                                                        speed={2}
                                                                        width='100%'
                                                                        height='40'
                                                                        backgroundColor={theme.skeleton.backgroundColor}
                                                                        foregroundColor={theme.skeleton.foregroundColor}
                                                                    >
                                                                        <rect x='0' y='0' rx='4' ry='4' width='100%' height='14' />
                                                                        <rect x='0' y='22' rx='4' ry='4' width='70%' height='14' />
                                                                    </ContentLoader>
                                                                ) : info.count > 0 ? (
                                                                    <PanelItem
                                                                        $clickable
                                                                        onClick={() =>
                                                                            handleGoToFullTool('/tools/template-product-selection/templates/' + product.modelID)
                                                                        }
                                                                    >
                                                                        <ItemDot $color='#3b82f6' />
                                                                        <ItemName>
                                                                            {info.count}{' '}
                                                                            {info.count === 1
                                                                                ? T._('template assigned', 'Admin')
                                                                                : T._('templates assigned', 'Admin')}
                                                                        </ItemName>
                                                                        <ItemAction>{T._('Manage', 'Admin')} &#8250;</ItemAction>
                                                                    </PanelItem>
                                                                ) : (
                                                                    <>
                                                                        <EmptyState>
                                                                            {tool.description}
                                                                            {tool.helpUrl && (
                                                                                <>
                                                                                    {' — '}
                                                                                    <LearnMoreLink href={tool.helpUrl} target='_blank' rel='noopener'>
                                                                                        {T._('Learn more', 'Admin')} &#8250;
                                                                                    </LearnMoreLink>
                                                                                </>
                                                                            )}
                                                                        </EmptyState>
                                                                        <PanelItem
                                                                            $clickable
                                                                            onClick={() =>
                                                                                handleGoToFullTool(
                                                                                    '/tools/template-product-selection/templates/' + product.modelID
                                                                                )
                                                                            }
                                                                        >
                                                                            <ItemDot $color='#d1d5db' />
                                                                            <ItemNameAction>{T._('Manage templates', 'Admin')} &#8250;</ItemNameAction>
                                                                        </PanelItem>
                                                                    </>
                                                                )}
                                                            </CollapsibleSection>
                                                        );
                                                    }
                                                    // Ad-hoc rendering for images and clipart
                                                    if (tool.key === 'images') {
                                                        const restrictions = toolData.imageRestrictions || [];
                                                        const anyEnabled = restrictions.some((restriction) => restriction.clipartGalleries);
                                                        return (
                                                            <CollapsibleSection
                                                                key={`${tool.key}-${expandAllKey}`}
                                                                title={tool.name}
                                                                count={anyEnabled ? 1 : 0}
                                                                badgeLabel={anyEnabled ? '✓' : '—'}
                                                                badgeBg={anyEnabled ? '#dbeafe' : '#f3f4f6'}
                                                                badgeFg={anyEnabled ? '#1e40af' : '#6b7280'}
                                                                helpUrl={tool.helpUrl}
                                                                onOpenChange={(isOpen) => {
                                                                    setOpenSections((prev) => {
                                                                        const next = new Set(prev);
                                                                        if (isOpen) next.add(tool.key);
                                                                        else next.delete(tool.key);
                                                                        return next;
                                                                    });
                                                                }}
                                                            >
                                                                {!toolData.loaded ? (
                                                                    <ContentLoader
                                                                        speed={2}
                                                                        width='100%'
                                                                        height='40'
                                                                        backgroundColor={theme.skeleton.backgroundColor}
                                                                        foregroundColor={theme.skeleton.foregroundColor}
                                                                    >
                                                                        <rect x='0' y='0' rx='4' ry='4' width='100%' height='14' />
                                                                        <rect x='0' y='22' rx='4' ry='4' width='70%' height='14' />
                                                                    </ContentLoader>
                                                                ) : restrictions.length > 0 ? (
                                                                    <>
                                                                        {restrictions.map((restriction) => (
                                                                            <PanelItem key={restriction.printMethodId}>
                                                                                <ItemDot $color={restriction.clipartGalleries ? '#10b981' : '#d1d5db'} />
                                                                                <ItemName>
                                                                                    {restriction.printMethodName}
                                                                                    <ItemMeta>
                                                                                        {T._('Gallery', 'Admin')}:{' '}
                                                                                        {restriction.clipartGalleries
                                                                                            ? T._('Enabled', 'Admin')
                                                                                            : T._('Disabled', 'Admin')}
                                                                                    </ItemMeta>
                                                                                </ItemName>
                                                                            </PanelItem>
                                                                        ))}
                                                                        <PanelItem $clickable onClick={() => handleGoToFullTool('/tools/images')}>
                                                                            <ItemDot $color='#d1d5db' />
                                                                            <ItemNameAction>{T._('Manage galleries', 'Admin')} &#8250;</ItemNameAction>
                                                                        </PanelItem>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <EmptyState>
                                                                            {tool.description}
                                                                            {tool.helpUrl && (
                                                                                <>
                                                                                    {' — '}
                                                                                    <LearnMoreLink href={tool.helpUrl} target='_blank' rel='noopener'>
                                                                                        {T._('Learn more', 'Admin')} &#8250;
                                                                                    </LearnMoreLink>
                                                                                </>
                                                                            )}
                                                                        </EmptyState>
                                                                        <PanelItem $clickable onClick={() => handleGoToFullTool('/tools/images')}>
                                                                            <ItemDot $color='#d1d5db' />
                                                                            <ItemNameAction>{T._('Manage galleries', 'Admin')} &#8250;</ItemNameAction>
                                                                        </PanelItem>
                                                                    </>
                                                                )}
                                                            </CollapsibleSection>
                                                        );
                                                    }
                                                    const isPrintingMethods = tool.key === 'printing-methods';
                                                    const isPricing = tool.key === 'pricing';
                                                    const isCollage = tool.key === 'collage';
                                                    const isProfanity = tool.key === 'profanity';
                                                    // For printing methods: check if assignable methods exist in the account
                                                    const assignedIds = isPrintingMethods
                                                        ? new Set((product.printingMethods || []).map((printMethod) => printMethod.id))
                                                        : new Set<string>();
                                                    const hasAssignableMethods =
                                                        isPrintingMethods &&
                                                        accountPrintMethods !== null &&
                                                        accountPrintMethods.some((printMethod) => !assignedIds.has(printMethod.id));
                                                    const accountHasCustomMethods =
                                                        isPrintingMethods && accountPrintMethods !== null && accountPrintMethods.length > 0;
                                                    // For pricing: check if assignable rules exist in the account
                                                    const assignedPricingIds = isPricing
                                                        ? new Set((toolData.pricingRules || []).map((pricingRule) => pricingRule.id))
                                                        : new Set<string>();
                                                    const hasAssignablePricingRules =
                                                        isPricing &&
                                                        accountPricingRules !== null &&
                                                        accountPricingRules.some((pricingRule) => !assignedPricingIds.has(pricingRule.id));
                                                    const accountHasCustomPricingRules =
                                                        isPricing && accountPricingRules !== null && accountPricingRules.length > 0;
                                                    const isComposerPricing = isPricing && product.zakekeEnvironment === 'COMPOSER';
                                                    const handleItemClick = (item: { id: string; name: string }) => {
                                                        handleOpenToolEditor(tool.key, item.id);
                                                    };
                                                    const isAreaBackground = tool.key === 'area-background';
                                                    const isAIStaging = tool.key === 'ai-lifestyle-scenes';
                                                    const isAIVirtualModel = tool.key === 'ai-try-on';
                                                    const isMoq = tool.key === 'moq';
                                                    const isQuote = tool.key === 'get-a-quote';
                                                    const handleAddClick = () => {
                                                        if (isPrintingMethods && hasAssignableMethods) {
                                                            setShowAddPrintingMethod(true);
                                                        } else if (isPrintingMethods) {
                                                            navigate(backofficeUrl(`/printing-method/?fromProductId=${product.modelID}`), {
                                                                state: { nextPrintMethodNumber: 1, ...productState }
                                                            });
                                                        } else if (isPricing && hasAssignablePricingRules) {
                                                            setShowAddPricingRule(true);
                                                        } else if (isPricing) {
                                                            navigate(backofficeUrl('/pricing-advanced'), { state: productState });
                                                        } else if (isCollage && accountHasCollageRules) {
                                                            setShowAddCollageRule(true);
                                                        } else if (isCollage) {
                                                            navigate(backofficeUrl('/tools/collage-layout/grid-rule/'));
                                                        } else if (isAreaBackground && accountHasAreaBackgroundRules) {
                                                            setShowAddAreaBackgroundRule(true);
                                                        } else if (isAreaBackground) {
                                                            navigate(backofficeUrl('/tools/area-background/rule/'));
                                                        } else if (isProfanity && accountHasProfanityRules) {
                                                            setShowAddProfanityRule(true);
                                                        } else if (isProfanity) {
                                                            navigate(backofficeUrl('/tools/profanity-filter-rules'));
                                                        } else if (isAIStaging && accountHasAIStagingRules) {
                                                            setShowAddAIStagingRule(true);
                                                        } else if (isAIStaging) {
                                                            navigate(backofficeUrl('/ai/product-staging-rule'));
                                                        } else if (isAIVirtualModel && accountHasAIVirtualModelRules) {
                                                            setShowAddAIVirtualModelRule(true);
                                                        } else if (isAIVirtualModel) {
                                                            navigate(backofficeUrl('/ai/virtual-model-rule'));
                                                        } else if (isMoq && accountHasMoqRules) {
                                                            setShowAddMoqRule(true);
                                                        } else if (isMoq) {
                                                            navigate(backofficeUrl('/moq/rule'));
                                                        } else if (isQuote && accountHasQuoteRules) {
                                                            setShowAddQuoteRule(true);
                                                        } else if (isQuote) {
                                                            navigate(backofficeUrl('/tools/get-a-quote/quote-rule/'));
                                                        } else if (tool.buildSetupRoute) {
                                                            navigate(backofficeUrl(tool.buildSetupRoute(product.modelID.toString())), {
                                                                state: productState
                                                            });
                                                        } else if (tool.newRoute) {
                                                            handleGoToFullTool(tool.newRoute);
                                                        } else {
                                                            handleGoToFullTool(tool.route);
                                                        }
                                                    };
                                                    const hasItems = info.items.length > 0;
                                                    const canAddMore =
                                                        tool.newRoute ||
                                                        tool.buildSetupRoute ||
                                                        isPrintingMethods ||
                                                        isPricing ||
                                                        isCollage ||
                                                        isAreaBackground ||
                                                        isProfanity ||
                                                        isAIStaging ||
                                                        isAIVirtualModel ||
                                                        isMoq ||
                                                        isQuote;
                                                    return (
                                                        <CollapsibleSection
                                                            key={`${tool.key}-${expandAllKey}`}
                                                            title={tool.name}
                                                            count={info.count}
                                                            badgeLabel={BOOLEAN_TOOLS.includes(tool.key) ? (info.count > 0 ? '✓' : '—') : undefined}
                                                            badgeBg={info.count > 0 ? '#dbeafe' : '#f3f4f6'}
                                                            badgeFg={info.count > 0 ? '#1e40af' : '#6b7280'}
                                                            helpUrl={tool.helpUrl}
                                                            onOpenChange={(isOpen) => {
                                                                setOpenSections((prev) => {
                                                                    const next = new Set(prev);
                                                                    if (isOpen) next.add(tool.key);
                                                                    else next.delete(tool.key);
                                                                    return next;
                                                                });
                                                            }}
                                                        >
                                                            {!toolData.loaded ? (
                                                                <ContentLoader
                                                                    speed={2}
                                                                    width='100%'
                                                                    height='40'
                                                                    backgroundColor={theme.skeleton.backgroundColor}
                                                                    foregroundColor={theme.skeleton.foregroundColor}
                                                                >
                                                                    <rect x='0' y='0' rx='4' ry='4' width='100%' height='14' />
                                                                    <rect x='0' y='22' rx='4' ry='4' width='70%' height='14' />
                                                                </ContentLoader>
                                                            ) : hasItems ? (
                                                                <>
                                                                    {isComposerPricing && (
                                                                        <EmptyState>
                                                                            {T._('These rules apply to the design part.', 'Admin')}{' '}
                                                                            {T._('For pricing the configuration part, follow', 'Admin')}{' '}
                                                                            <LearnMoreLink
                                                                                href='https://zakeke.zendesk.com/hc/en-us/articles/360009576134-Composer#h_01G0M6P43K3A6F8NHZBFVHP02J'
                                                                                target='_blank'
                                                                                rel='noopener'
                                                                            >
                                                                                {T._('this guide', 'Admin')} &#8250;
                                                                            </LearnMoreLink>
                                                                        </EmptyState>
                                                                    )}
                                                                    {info.items.slice(0, 10).map((item) => {
                                                                        const isItemUnassigning = isUnassigning && unassigningItemId === item.id;
                                                                        return (
                                                                            <PanelItemDimmed
                                                                                key={item.id}
                                                                                $clickable={!isItemUnassigning}
                                                                                onClick={() => !isItemUnassigning && handleItemClick(item)}
                                                                                $dimmed={isItemUnassigning}
                                                                            >
                                                                                {item.previewUrl ? (
                                                                                    <ItemPreviewImg src={item.previewUrl} alt={item.name} />
                                                                                ) : (
                                                                                    <ItemDot $color='#3b82f6' />
                                                                                )}
                                                                                <ItemName>{item.name}</ItemName>
                                                                                <ItemActionDanger
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        if (isPrintingMethods) {
                                                                                            handleUnassignPrintMethod(item.id);
                                                                                        } else if (isPricing) {
                                                                                            handleUnassignPricingRule(item.id);
                                                                                        } else {
                                                                                            handleUnassignGenericTool(tool.key, item.id);
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    {T._('Unassign', 'Admin')}
                                                                                </ItemActionDanger>
                                                                                <ItemAction>{T._('Edit', 'Admin')} &#8250;</ItemAction>
                                                                            </PanelItemDimmed>
                                                                        );
                                                                    })}
                                                                    {info.items.length > 10 && (
                                                                        <PanelItem>
                                                                            <ItemMeta>
                                                                                +{info.items.length - 10} {T._('more', 'Admin')}
                                                                            </ItemMeta>
                                                                        </PanelItem>
                                                                    )}
                                                                    {canAddMore && (
                                                                        <PanelItemSpaced $clickable onClick={handleAddClick}>
                                                                            <ItemDot $color='#d1d5db' />
                                                                            <ItemNameAction>+ {T._('Add', 'Admin')}</ItemNameAction>
                                                                        </PanelItemSpaced>
                                                                    )}
                                                                </>
                                                            ) : info.status ? (
                                                                <PanelItem
                                                                    $clickable
                                                                    onClick={() => {
                                                                        if (tool.key === 'placement' && tool.buildSetupRoute) {
                                                                            navigate(backofficeUrl(tool.buildSetupRoute(product.modelID.toString())), {
                                                                                state: productState
                                                                            });
                                                                        } else if (tool.buildEmbeddedUrl) {
                                                                            handleOpenToolEditor(tool.key, '');
                                                                        } else if (tool.buildSetupRoute) {
                                                                            navigate(backofficeUrl(tool.buildSetupRoute(product.modelID.toString())), {
                                                                                state: productState
                                                                            });
                                                                        } else {
                                                                            handleGoToFullTool(tool.route);
                                                                        }
                                                                    }}
                                                                >
                                                                    <ItemDot $color='#10b981' />
                                                                    <ItemName>{info.status}</ItemName>
                                                                    <ItemAction>{T._('Manage', 'Admin')} &#8250;</ItemAction>
                                                                </PanelItem>
                                                            ) : (
                                                                <>
                                                                    <EmptyState>
                                                                        {isComposerPricing ? (
                                                                            <>
                                                                                {T._('Create price markup for the design part of your product.', 'Admin')}{' '}
                                                                                {T._('For pricing the configuration part, follow', 'Admin')}{' '}
                                                                                <LearnMoreLink
                                                                                    href='https://zakeke.zendesk.com/hc/en-us/articles/360009576134-Composer#h_01G0M6P43K3A6F8NHZBFVHP02J'
                                                                                    target='_blank'
                                                                                    rel='noopener'
                                                                                >
                                                                                    {T._('this guide', 'Admin')} &#8250;
                                                                                </LearnMoreLink>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                {tool.description}
                                                                                {tool.helpUrl && (
                                                                                    <>
                                                                                        {' — '}
                                                                                        <LearnMoreLink href={tool.helpUrl} target='_blank' rel='noopener'>
                                                                                            {T._('Learn more', 'Admin')} &#8250;
                                                                                        </LearnMoreLink>
                                                                                    </>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </EmptyState>
                                                                    <PanelItemSpacedLarge $clickable onClick={handleAddClick}>
                                                                        <ItemDot $color='#d1d5db' />
                                                                        <ItemNameAction>
                                                                            +{' '}
                                                                            {(isPrintingMethods && accountHasCustomMethods) ||
                                                                            (isPricing && accountHasCustomPricingRules) ||
                                                                            (isCollage && accountHasCollageRules) ||
                                                                            (isAreaBackground && accountHasAreaBackgroundRules) ||
                                                                            (isProfanity && accountHasProfanityRules) ||
                                                                            (isAIStaging && accountHasAIStagingRules) ||
                                                                            (isAIVirtualModel && accountHasAIVirtualModelRules) ||
                                                                            (isMoq && accountHasMoqRules) ||
                                                                            (isQuote && accountHasQuoteRules)
                                                                                ? T._('Add', 'Admin')
                                                                                : T._('Set up', 'Admin')}
                                                                        </ItemNameAction>
                                                                    </PanelItemSpacedLarge>
                                                                </>
                                                            )}
                                                        </CollapsibleSection>
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    })}
                                </PanelBody>
                            )}
                            {confirmingDelete && (
                                <DeleteOverlay onClick={() => setConfirmingDelete(false)}>
                                    <DeleteCard onClick={(e) => e.stopPropagation()}>
                                        <DeleteConfirmText>
                                            {T._('This will permanently delete', 'Admin')} <strong>{product.name}</strong>.
                                            <br />
                                            {T._('This action cannot be undone.', 'Admin')}
                                        </DeleteConfirmText>
                                        <DeleteConfirmActions>
                                            <FooterButton onClick={() => setConfirmingDelete(false)}>{T._('Cancel', 'Admin')}</FooterButton>
                                            <FooterButton
                                                $danger
                                                onClick={() => {
                                                    setConfirmingDelete(false);
                                                    onDelete!();
                                                }}
                                            >
                                                {T._('Delete', 'Admin')}
                                            </FooterButton>
                                        </DeleteConfirmActions>
                                    </DeleteCard>
                                </DeleteOverlay>
                            )}
                        </SidePanelScrollArea>
                    )
                }
            </SidePanelDrawer>
            {/* ===== STACKED PANEL (L2) ===== */}
            {stackedEditor && stackedEditor.type === 'printing-methods' && (
                <PrintingMethodEditor
                    productId={product.id}
                    printTypeId={stackedEditor.id}
                    onBack={handleCloseStacked}
                    onGoToFullTool={() => handleGoToFullTool('/printing-method', stackedEditor.id)}
                />
            )}
            {stackedEditor && stackedEditor.type === 'pricing' && (
                <PricingRuleEditor
                    pricingId={stackedEditor.id}
                    productId={product.id}
                    onBack={handleCloseStacked}
                    onGoToFullTool={() => handleGoToFullTool('/pricing-advanced?pricing_id=' + toLocalID(stackedEditor.id))}
                />
            )}
            {stackedEditor && stackedEditor.type === 'composer-attribute' && configuratorData && (
                <ComposerAttributeEditor
                    productName={product.name}
                    attributes={configuratorData.attributes}
                    sceneElements={[
                        ...configuratorData.sceneMeshes.map((mesh) => ({ id: mesh.meshID, name: mesh.name, type: 'mesh' as const })),
                        ...configuratorData.sceneMeshes.flatMap((m) =>
                            m.materials.map((mat) => ({ id: mat.effectID, name: mat.name, type: 'material' as const }))
                        )
                    ]}
                    initialAttribute={stackedEditor.id || undefined}
                    filterMode={stackedEditor.filterMode || 'all'}
                    filterOptionName={stackedEditor.optionName}
                    onBack={handleCloseStacked}
                    onGoToFullTool={() => handleGoToComposer()}
                />
            )}
            {stackedEditor && stackedEditor.type !== 'printing-methods' && stackedEditor.type !== 'pricing' && stackedEditor.type !== 'composer-attribute' && (
                <GenericRuleEditor
                    toolKey={stackedEditor.type}
                    itemId={stackedEditor.id}
                    productId={product.id}
                    onBack={handleCloseStacked}
                    onGoToFullTool={() => {
                        const tool = TOOL_DEFINITIONS.find((t) => t.key === stackedEditor.type);
                        if (tool?.buildSetupRoute) {
                            navigate(backofficeUrl(tool.buildSetupRoute(product.modelID.toString())), { state: productState });
                        } else {
                            let itemId = stackedEditor.id;
                            try {
                                const decoded = atob(itemId);
                                if (decoded.includes(':')) itemId = decoded.split(':')[1];
                            } catch {
                                // not base64, use as-is
                            }
                            handleGoToFullTool(tool?.detailRoute || tool?.route || '', itemId);
                        }
                    }}
                />
            )}
            {showAddPrintingMethod && (
                <AddPrintingMethodPanel
                    productId={product.id}
                    existingMethodIds={(product.printingMethods || []).map((printMethod) => printMethod.id)}
                    onBack={() => setShowAddPrintingMethod(false)}
                    onCreateNew={(nextNumber) => {
                        setShowAddPrintingMethod(false);
                        navigate(backofficeUrl(`/printing-method/?fromProductId=${product.modelID}`), {
                            state: { nextPrintMethodNumber: nextNumber, ...productState }
                        });
                    }}
                    onSelectExisting={(id) => {
                        setShowAddPrintingMethod(false);
                        handleAssignPrintMethod(id);
                    }}
                />
            )}
            {showAddPricingRule && (
                <AddPricingRulePanel
                    productId={product.id}
                    existingPricingIds={(toolData.pricingRules || []).map((pricingRule) => pricingRule.id)}
                    onBack={() => setShowAddPricingRule(false)}
                    onCreateNew={() => {
                        setShowAddPricingRule(false);
                        navigate(backofficeUrl('/pricing-advanced'), { state: productState });
                    }}
                    onSelectExisting={(id, name) => {
                        setShowAddPricingRule(false);
                        handleAssignPricingRule(id, name);
                    }}
                />
            )}
            {showAddCollageRule && (
                <AddCollageRulePanel
                    productId={product.modelID}
                    productRelayId={product.id}
                    existingRuleIds={(toolData.collageRules || []).map((rule) => rule.id)}
                    onBack={() => setShowAddCollageRule(false)}
                    onAssigned={() => {
                        setShowAddCollageRule(false);
                        setDataRefreshKey((k) => k + 1);
                    }}
                />
            )}
            {showAddProfanityRule && (
                <AddProfanityRulePanel
                    productId={product.modelID}
                    productRelayId={product.id}
                    existingRuleIds={(toolData.profanityRules || []).map((rule) => rule.id)}
                    onBack={() => setShowAddProfanityRule(false)}
                    onAssigned={() => {
                        setShowAddProfanityRule(false);
                        setDataRefreshKey((k) => k + 1);
                    }}
                />
            )}
            {showAddAreaBackgroundRule && (
                <AddAreaBackgroundRulePanel
                    productId={product.modelID}
                    productRelayId={product.id}
                    existingRuleIds={(toolData.areaBackgroundRules || []).map((rule) => rule.id)}
                    onBack={() => setShowAddAreaBackgroundRule(false)}
                    onAssigned={() => {
                        setShowAddAreaBackgroundRule(false);
                        setDataRefreshKey((k) => k + 1);
                    }}
                />
            )}
            {showAddAIStagingRule && (
                <AddAIStagingRulePanel
                    productId={product.modelID}
                    productRelayId={product.id}
                    existingRuleIds={(toolData.aiStagingRules || []).map((rule) => rule.id)}
                    onBack={() => setShowAddAIStagingRule(false)}
                    onAssigned={() => {
                        setShowAddAIStagingRule(false);
                        setDataRefreshKey((k) => k + 1);
                    }}
                />
            )}
            {showAddAIVirtualModelRule && (
                <AddAIVirtualModelRulePanel
                    productId={product.modelID}
                    productRelayId={product.id}
                    existingRuleIds={(toolData.aiVirtualModelRules || []).map((rule) => rule.id)}
                    onBack={() => setShowAddAIVirtualModelRule(false)}
                    onAssigned={() => {
                        setShowAddAIVirtualModelRule(false);
                        setDataRefreshKey((k) => k + 1);
                    }}
                />
            )}
            {showAddMoqRule && (
                <AddMoqRulePanel
                    productId={product.modelID}
                    productRelayId={product.id}
                    existingRuleIds={(toolData.moqRules || []).map((rule) => rule.id)}
                    onBack={() => setShowAddMoqRule(false)}
                    onAssigned={() => {
                        setShowAddMoqRule(false);
                        setDataRefreshKey((k) => k + 1);
                    }}
                />
            )}
            {showAddQuoteRule && (
                <AddQuoteRulePanel
                    productId={product.modelID}
                    productRelayId={product.id}
                    existingRuleId={toolData.quoteRule?.id || null}
                    onBack={() => setShowAddQuoteRule(false)}
                    onAssigned={() => {
                        setShowAddQuoteRule(false);
                        setDataRefreshKey((k) => k + 1);
                    }}
                />
            )}
        </>
    );
};
